import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { useDropzone } from "react-dropzone";
import PageWrapper from "../../components/layout/PageWrapper";
import Avatar from "../../components/ui/Avatar";
import Button from "../../components/forms/Button";
import BaseModal from "../../components/modals/BaseModal";
import { axiosPrivate } from "../../api/axios";
import chatService from "../../api/chatService";
import useAuth from "../../hooks/useAuth";
import useChatSocket from "../../hooks/useChatSocket";
import useToast from "../../hooks/useToast";
import useMessageCache from "../../hooks/useMessageCache";
import {
  uploadPastedImage,
  uploadToSupabase,
  validateFile,
} from "../../utils/storage";
import { uploadFile, IMAGES_BUCKET } from "../../api/supabaseStorage";
import JSZip from "jszip";
import { getTextDirectionStyle } from "../../utils/textDirection";
import messageSentSound from "../../assets/sounds/chat/message-sent.mp3";
import messageReceiveSound from "../../assets/sounds/chat/message-recive.mp3";
import {
  Send,
  Plus,
  X,
  ChevronDown,
  Hash,
  Loader2,
  Users,
  Trash2,
  Clock,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  Paperclip,
  FileText,
  Download,
  ArrowDown,
  Search,
  Reply,
  Edit2,
  FolderOpen,
} from "lucide-react";

const TEAMS_OPTIONS = [
  { value: "high_potit", label: "High Potential" },
  { value: "backend", label: "Backend Team" },
  { value: "frontend", label: "Frontend Team" },
  { value: "mobile_app", label: "Mobile App Team" },
  { value: "uiux", label: "UI/UX Team" },
  { value: "tester", label: "Tester Team" },
  { value: "ra", label: "RA Team" },
];

/** Render message content with @mentions styled (matches backend parseMentions format) */
function renderContentWithMentions(content, isOwnMessage) {
  if (!content || typeof content !== "string") return null;

  // Match URLs first, then @mentions
  const tokenRegex =
    /(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+\.[^\s<>"]+)|(@[\w-]+(?:\s+[\w-]+)*)/g;

  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = tokenRegex.exec(content)) !== null) {
    // Plain text before this token
    if (match.index > lastIndex) {
      parts.push({
        type: "text",
        value: content.slice(lastIndex, match.index),
      });
    }

    if (match[1]) {
      // URL token
      const href = match[1].startsWith("http")
        ? match[1]
        : `https://${match[1]}`;
      parts.push({ type: "url", value: match[1], href });
    } else if (match[2]) {
      // Mention token
      parts.push({ type: "mention", value: match[2] });
    }

    lastIndex = tokenRegex.lastIndex;
  }

  if (lastIndex < content.length) {
    parts.push({ type: "text", value: content.slice(lastIndex) });
  }

  if (parts.length === 0) return content;

  return parts.map((part, i) => {
    if (part.type === "mention") {
      return (
        <span
          key={i}
          className={
            isOwnMessage
              ? "font-semibold text-white/95"
              : "font-semibold text-primary-accent"
          }
        >
          {part.value}
        </span>
      );
    }
    if (part.type === "url") {
      return (
        <a
          key={i}
          href={part.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`underline underline-offset-2 break-all ${
            isOwnMessage
              ? "text-white/90 hover:text-white"
              : "text-primary-accent hover:text-primary-accent/80"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {part.value}
        </a>
      );
    }
    return <React.Fragment key={i}>{part.value}</React.Fragment>;
  });
}

const GroupChat = () => {
  const { auth } = useAuth();
  const token = auth?.accessToken || localStorage.getItem("accessToken");
  const currentUserId = auth?.employee?._id;
  const { showError, showSuccess } = useToast();

  const {
    onlineUserIds,
    isConnected,
    joinChannel,
    leaveChannel,
    sendMessage,
    deleteMessage,
    onMessageNew,
    onMessageDeleted,
    addReaction,
    removeReaction,
    onReactionAdded,
    onReactionRemoved,
    editMessage,
    onMessageEdited,
    onMessageRead,
    markMessageSeen: markMessageSeenSocket,
    onMessageSeen,
    markChannelAsRead: markChannelAsReadSocket,
    onChannelRead,
  } = useChatSocket({ token });

  const {
    getCachedMessages,
    isPageLoaded,
    addMessages,
    addNewMessage,
    updateMessage,
    clearCache,
    getOldestPage,
  } = useMessageCache();

  const [channelsList, setChannelsList] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [channelMembers, setChannelMembers] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [folderConfirmation, setFolderConfirmation] = useState(null); // { name, fileCount, files: File[] } | null
  const [folderZipping, setFolderZipping] = useState(false);
  const [folderUploading, setFolderUploading] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [, setPendingMessages] = useState(new Map()); // tempId -> { tempId, content, timestamp }
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0); // unread in current channel when scrolled up
  const [channelUnreadCounts, setChannelUnreadCounts] = useState({}); // { [channelId]: count } for other channels
  const lastSeenMessageIdRef = useRef(null);
  // Rate limiting: track message timestamps (persistent across channel switches)
  const messageTimestampsRef = useRef([]);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitEndTime, setRateLimitEndTime] = useState(null);
  const rateLimitTimerRef = useRef(null);

  // Reaction picker state
  const [showReactionPicker, setShowReactionPicker] = useState(null); // messageId or null
  const reactionPickerRef = useRef(null);

  // Read receipt popover state (messageId or null)
  const [readReceiptPopover, setReadReceiptPopover] = useState(null);
  // Whether the seen list is expanded inside the popover
  const [seenModal, setSeenModal] = useState({ open: false, msg: null });
  // Track which message IDs we've already emitted message:seen for (avoid duplicate emits)
  const seenMessageIdsRef = useRef(new Set());
  // Queue seen events that arrive before message:new has resolved the temp message
  // Map<realMessageId, { userId, readAt }[]>
  const pendingSeenEventsRef = useRef(new Map());

  // Initialize rate limit state from localStorage on mount
  useEffect(() => {
    const savedRateLimitEndTime = localStorage.getItem("chat_rateLimitEndTime");
    if (savedRateLimitEndTime) {
      const endTime = parseInt(savedRateLimitEndTime, 10);
      const now = Date.now();
      if (now < endTime) {
        // Still rate limited
        setIsRateLimited(true);
        setRateLimitEndTime(endTime);
        // Set timer to unblock when time expires
        const timeLeft = endTime - now;
        rateLimitTimerRef.current = setTimeout(() => {
          setIsRateLimited(false);
          setRateLimitEndTime(null);
          messageTimestampsRef.current = [];
          localStorage.removeItem("chat_rateLimitEndTime");
        }, timeLeft);
      } else {
        // Rate limit expired, clean up
        localStorage.removeItem("chat_rateLimitEndTime");
      }
    }

    // Load message timestamps from localStorage
    const savedTimestamps = localStorage.getItem("chat_messageTimestamps");
    if (savedTimestamps) {
      try {
        const timestamps = JSON.parse(savedTimestamps);
        const oneMinuteAgo = Date.now() - 60000;
        // Filter out old timestamps
        const validTimestamps = timestamps.filter((ts) => ts > oneMinuteAgo);
        messageTimestampsRef.current = validTimestamps;
        if (validTimestamps.length !== timestamps.length) {
          localStorage.setItem(
            "chat_messageTimestamps",
            JSON.stringify(validTimestamps),
          );
        }
      } catch (e) {
        console.warn("Failed to load message timestamps from localStorage", e);
      }
    }
  }, []);
  const pendingIdCounterRef = useRef(0);
  const messageSentAudioRef = useRef(null);
  const messageReceiveAudioRef = useRef(null);
  const [isCreateChannelModalOpen, setIsCreateChannelModalOpen] =
    useState(false);
  const [isAddMembersModalOpen, setIsAddMembersModalOpen] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  const [selectedMembersToAdd, setSelectedMembersToAdd] = useState([]);
  const [isMembersDropdownOpen, setIsMembersDropdownOpen] = useState(false);
  const [isTeamsDropdownOpen, setIsTeamsDropdownOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [createChannelMemberSearch, setCreateChannelMemberSearch] =
    useState("");
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [channelSearchQuery, setChannelSearchQuery] = useState("");
  const [newChannel, setNewChannel] = useState({
    name: "",
    displayName: "",
    description: "",
    memberIds: [],
    teamIds: [],
  });
  const [creating, setCreating] = useState(false);
  const membersDropdownRef = useRef(null);
  const teamsDropdownRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevChannelIdRef = useRef(null);
  const messageInputRef = useRef(null);
  const checkIfAtBottomRef = useRef(null);
  const MESSAGES_PER_PAGE = 30;
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    open: false,
    channelId: null,
  });
  const [deleting, setDeleting] = useState(false);
  // @ mention autocomplete
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(0);
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const mentionDropdownRef = useRef(null);

  // Reply, Edit, Delete features
  const [replyingTo, setReplyingTo] = useState(null); // { _id, content, sender }
  const [editingMessage, setEditingMessage] = useState(null); // { _id, content }
  const [deleteMessageModal, setDeleteMessageModal] = useState({
    open: false,
    messageId: null,
  });

  // Check if user is at bottom and update state (defined early so channel-change effect can depend on it)
  const checkIfAtBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      setIsAtBottom(true);
      return false;
    }
    const threshold = 150;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isNearBottom = distanceFromBottom < threshold;
    setIsAtBottom(isNearBottom);
    if (isNearBottom && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?._id) {
        lastSeenMessageIdRef.current = lastMessage._id;
        setUnreadCount(0);
      }
    }
    return isNearBottom;
  }, [messages]);
  checkIfAtBottomRef.current = checkIfAtBottom;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        membersDropdownRef.current &&
        !membersDropdownRef.current.contains(e.target)
      )
        setIsMembersDropdownOpen(false);
      if (
        teamsDropdownRef.current &&
        !teamsDropdownRef.current.contains(e.target)
      )
        setIsTeamsDropdownOpen(false);
      if (
        mentionDropdownRef.current &&
        !mentionDropdownRef.current.contains(e.target)
      )
        setShowMentionDropdown(false);
      // Close read receipt popover when clicking outside
      if (readReceiptPopover) {
        setReadReceiptPopover(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [readReceiptPopover]);

  // Initialize audio elements
  useEffect(() => {
    messageSentAudioRef.current = new Audio(messageSentSound);
    messageReceiveAudioRef.current = new Audio(messageReceiveSound);
    // Set volume (0.0 to 1.0)
    messageSentAudioRef.current.volume = 0.5;
    messageReceiveAudioRef.current.volume = 0.5;

    return () => {
      // Cleanup
      if (messageSentAudioRef.current) {
        messageSentAudioRef.current.pause();
        messageSentAudioRef.current = null;
      }
      if (messageReceiveAudioRef.current) {
        messageReceiveAudioRef.current.pause();
        messageReceiveAudioRef.current = null;
      }
    };
  }, []);

  const selectedChannel = channelsList.find(
    (ch) => ch._id === selectedChannelId,
  );

  // Fetch channels on mount
  useEffect(() => {
    let cancelled = false;
    setLoadingChannels(true);
    chatService
      .getChannels()
      .then((response) => {
        if (cancelled || !response) return;
        const list = Array.isArray(response.channels)
          ? response.channels
          : Array.isArray(response)
            ? response
            : [];
        setChannelsList(list);

        // Join all channels on mount so user receives messages from all channels
        list.forEach((channel) => {
          const channelId = channel._id?.toString?.() || String(channel._id);
          joinChannel(channelId);
        });
      })
      .catch((err) => {
        if (!cancelled) {
          showError(err?.response?.data?.message || "Failed to load channels");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingChannels(false);
      });
    // Also fetch per-channel preferences for current user
    return () => {
      cancelled = true;
    };
  }, [showError, joinChannel]);

  // When a new channel is created (via Firebase push), refresh channels list and join the new channel
  useEffect(() => {
    const handleChannelCreated = async () => {
      try {
        setLoadingChannels(true);
        const response = await chatService.getChannels();
        if (!response) return;
        const newList = Array.isArray(response.channels)
          ? response.channels
          : Array.isArray(response)
            ? response
            : [];

        // Find newly added channels and join them
        const oldChannelIds = new Set(
          channelsList.map((ch) => ch._id?.toString?.() || String(ch._id)),
        );
        newList.forEach((channel) => {
          const channelId = channel._id?.toString?.() || String(channel._id);
          if (!oldChannelIds.has(channelId)) {
            // This is a new channel, join it
            joinChannel(channelId);
          }
        });

        setChannelsList(newList);
      } catch (err) {
        console.warn(
          "Failed to refresh channels after channel_created event",
          err,
        );
      } finally {
        setLoadingChannels(false);
      }
    };
    window.addEventListener("chat-channel-created", handleChannelCreated);
    return () =>
      window.removeEventListener("chat-channel-created", handleChannelCreated);
  }, [channelsList, joinChannel]);

  // When selected channel changes: fetch messages and members (stay joined to all channels)
  // Members are fetched only on channel change; online status comes from socket (onlineUserIds) and does not refetch.
  useEffect(() => {
    prevChannelIdRef.current = selectedChannelId;

    if (!selectedChannelId) {
      setMessages([]);
      setChannelMembers([]);
      return;
    }

    const channelIdForEffect = selectedChannelId;
    let cancelled = false;

    joinChannel(selectedChannelId);
    setLoadingMembers(true);
    setLoadingMessages(true);
    setHasMorePages(true);

    // Always fetch latest messages when opening a channel to ensure we have the most recent data
    // This ensures socket message history sync works without refresh
    chatService
      .getChannelMessages(channelIdForEffect, 1, MESSAGES_PER_PAGE)
      .then((msgs) => {
        if (cancelled) return;
        const messagesWithStatus = (Array.isArray(msgs) ? msgs : []).map(
          (msg) => ({
            ...msg,
            status: "delivered",
          }),
        );
        const updatedMessages = addMessages(
          channelIdForEffect,
          messagesWithStatus,
          1,
        );
        setMessages(updatedMessages);
        // If we got fewer than MESSAGES_PER_PAGE, there are no more messages
        setHasMorePages(msgs.length === MESSAGES_PER_PAGE);
        // Reset scroll state on channel open
        setIsAtBottom(true);
        setUnreadCount(0);
        if (updatedMessages.length > 0) {
          const lastMessage = updatedMessages[updatedMessages.length - 1];
          if (lastMessage?._id) {
            lastSeenMessageIdRef.current = lastMessage._id;
          }
        }

        // Scroll to bottom after messages are loaded and rendered
        setTimeout(() => {
          if (cancelled) return;
          const container = messagesContainerRef.current;
          if (container && messagesEndRef.current) {
            // Force scroll to bottom
            messagesEndRef.current.scrollIntoView({ behavior: "auto" });
            setIsAtBottom(true);
            setUnreadCount(0);
            // Double-check scroll position after a short delay to ensure it worked
            setTimeout(() => {
              if (cancelled) return;
              checkIfAtBottomRef.current?.();
              // If still not at bottom, try scrolling again
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
              }
            }, 300);
          }
        }, 200);
      })
      .catch((err) => {
        if (!cancelled) {
          showError(err?.response?.data?.message || "Failed to load messages");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingMessages(false);
        }
      });

    // Fetch members once per channel change; avoid setting state if channel changed before request completed
    chatService
      .getChannelMembers(channelIdForEffect)
      .then((members) => {
        if (!cancelled) {
          // Sort members alphabetically by name (A-Z)
          const sortedMembers = Array.isArray(members)
            ? members.sort((a, b) => {
                const nameA = (a.fullNameEn || "").toLowerCase();
                const nameB = (b.fullNameEn || "").toLowerCase();
                return nameA.localeCompare(nameB);
              })
            : [];
          setChannelMembers(sortedMembers);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          showError(
            err?.response?.data?.message || "Failed to load channel members",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingMembers(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelId, showError, addMessages]);

  // Real-time new message
  useEffect(() => {
    const unsub = onMessageNew((message) => {
      if (!message) return;

      // Extract channelId from message - handle various formats
      const channelId =
        message?.channel?._id?.toString?.() ||
        message?.channel?._id ||
        message?.channel?.toString?.() ||
        message?.channel ||
        message?.channelId?.toString?.() ||
        message?.channelId;

      if (!channelId) {
        console.warn("[GroupChat] Message missing channelId:", message);
        return;
      }

      // Normalize channelId to string for consistent comparison
      const channelIdStr = String(channelId);
      const selectedIdStr = selectedChannelId
        ? String(selectedChannelId)
        : null;

      const isOwnMessage =
        message.sender?._id?.toString() === currentUserId?.toString() ||
        message.sender?._id === currentUserId ||
        message.sender === currentUserId;

      // Message for a different channel: bump per-channel unread counter
      // Only increment if it's not from the current user (don't count own messages as unread)
      if ((!selectedIdStr || channelIdStr !== selectedIdStr) && !isOwnMessage) {
        console.log(
          "[GroupChat] Message for other channel - incrementing unread:",
          {
            messageChannelId: channelIdStr,
            currentChannelId: selectedIdStr,
            sender: message.sender?.fullNameEn,
            isOwnMessage,
          },
        );

        // Find the matching channel ID format from channelsList
        const matchingChannel = channelsList.find((ch) => {
          const chIdStr = ch._id?.toString?.() || String(ch._id);
          return chIdStr === channelIdStr;
        });

        // Use the normalized channel ID from channelsList if found, otherwise use the one from message
        const normalizedChannelId = matchingChannel
          ? matchingChannel._id?.toString?.() || String(matchingChannel._id)
          : channelIdStr;

        setChannelUnreadCounts((prev) => {
          const next = { ...prev };
          const currentCount = next[normalizedChannelId] || 0;
          next[normalizedChannelId] = currentCount + 1;
          console.log(
            "[GroupChat] Incremented unread count for channel:",
            normalizedChannelId,
            "new count:",
            next[normalizedChannelId],
          );
          return next;
        });
        return;
      }

      // For current channel: check if message already exists in cache (avoid duplicates)
      const existingMessages = getCachedMessages(selectedChannelId);
      const alreadyExists = existingMessages.some(
        (m) => m._id && message._id && m._id === message._id,
      );

      if (alreadyExists) {
        return; // Skip true duplicate
      }

      // Check if this is a confirmation of a pending message
      // Match by content, sender, and timestamp (within 5 seconds) for better accuracy
      let matchedTempId = null;
      const messageTime = new Date(message.createdAt).getTime();

      setPendingMessages((prevPending) => {
        const newPending = new Map(prevPending);
        // Find matching pending message (same content, sender, and within 5 seconds)
        // Sort by timestamp to match the closest one
        const pendingEntries = Array.from(newPending.entries())
          .map(([tempId, pending]) => ({
            tempId,
            pending,
            time: new Date(pending.timestamp).getTime(),
          }))
          .sort(
            (a, b) =>
              Math.abs(a.time - messageTime) - Math.abs(b.time - messageTime),
          );

        for (const { tempId, pending } of pendingEntries) {
          const pendingTime = new Date(pending.timestamp).getTime();
          const timeDiff = Math.abs(messageTime - pendingTime);

          const senderMatches =
            pending.senderId?.toString() === message.sender?._id?.toString();
          const contentMatches = pending.content === (message.content || "");
          const timeMatches = timeDiff < 5000;
          // For image-only messages, also verify the attachmentUrl matches to
          // avoid false positives when multiple images are sent in quick succession
          const attachmentUrlMatches = pending.attachmentUrl
            ? pending.attachmentUrl === message.attachmentUrl
            : true;

          if (
            senderMatches &&
            contentMatches &&
            timeMatches &&
            attachmentUrlMatches
          ) {
            matchedTempId = tempId;
            newPending.delete(tempId);
            break;
          }
        }
        return newPending;
      });

      // If this matches a pending message, replace it; otherwise add as new
      if (matchedTempId) {
        const realId = message._id?.toString();

        // Replace pending temp with server-confirmed message.
        // updateMessage does readBy union-merge + status priority inside the cache.
        let finalMessages = updateMessage(selectedChannelId, matchedTempId, {
          ...message,
          status: "delivered",
          tempId: undefined,
        });

        // ── Drain seen events that arrived before message:new ─────────────
        if (realId) {
          const queued = pendingSeenEventsRef.current.get(realId);
          if (queued?.length) {
            pendingSeenEventsRef.current.delete(realId);
            for (const evt of queued) {
              // updateMessage union-merges into cache
              updateMessage(selectedChannelId, realId, {
                readBy: [
                  {
                    userId: { _id: evt.userId },
                    readAt: evt.readAt || new Date().toISOString(),
                  },
                ],
              });
            }
            // Re-read from cache now that all queued entries are merged in
            finalMessages = getCachedMessages(selectedChannelId);
          }
        }

        setMessages(finalMessages);
      } else {
        // Add new message (from another user or unmatched)
        const updatedMessages = addNewMessage(selectedChannelId, {
          ...message,
          status: "delivered",
        });
        setMessages(updatedMessages);

        // Check if user is not at bottom, increment unread count
        const container = messagesContainerRef.current;
        if (container) {
          const threshold = 150;
          const isNearBottom =
            container.scrollHeight -
              container.scrollTop -
              container.clientHeight <
            threshold;
          if (!isNearBottom) {
            setUnreadCount((prev) => prev + 1);
          }
        }
      }

      // Play receive sound only for messages from others (not our own)
      if (!isOwnMessage) {
        try {
          if (messageReceiveAudioRef.current) {
            messageReceiveAudioRef.current.currentTime = 0; // Reset to start
            messageReceiveAudioRef.current.play().catch((err) => {
              console.warn("Failed to play receive sound:", err);
            });
          }
        } catch (err) {
          console.warn("Error playing receive sound:", err);
        }

        // Auto-mark as seen if this is the active channel and the tab is visible
        if (
          message._id &&
          channelIdStr === selectedIdStr &&
          document.visibilityState === "visible" &&
          !seenMessageIdsRef.current.has(message._id.toString())
        ) {
          seenMessageIdsRef.current.add(message._id.toString());
          markMessageSeenSocket(message._id.toString(), channelIdStr);
        }
      }
    });
    return unsub;
  }, [
    onMessageNew,
    selectedChannelId,
    currentUserId,
    addNewMessage,
    updateMessage,
    getCachedMessages,
    channelsList,
    markMessageSeenSocket,
  ]);

  // Real-time message deletion
  useEffect(() => {
    const unsub = onMessageDeleted((payload) => {
      if (!payload || !payload.messageId) return;

      const { messageId, channelId } = payload;
      const channelIdStr = String(channelId || "");
      const selectedIdStr = selectedChannelId
        ? String(selectedChannelId)
        : null;

      // If this is the currently selected channel, remove the message
      if (channelIdStr === selectedIdStr) {
        setMessages((prev) =>
          prev.filter((msg) => {
            const msgId = msg._id?.toString() || msg.tempId?.toString();
            return msgId !== messageId.toString();
          }),
        );
      }

      // Also remove from cache
      const cached = getCachedMessages(channelIdStr);
      if (cached) {
        cached.filter((msg) => {
          const msgId = msg._id?.toString() || msg.tempId?.toString();
          return msgId !== messageId.toString();
        });
        // Note: Cache filtering logic here if needed in the future
      }
    });
    return unsub;
  }, [onMessageDeleted, selectedChannelId, getCachedMessages]);

  // Real-time reaction updates
  useEffect(() => {
    const unsubAdded = onReactionAdded((payload) => {
      if (!payload || !payload.messageId) return;
      const { messageId, reactions } = payload;

      // Update the message in the current view
      setMessages((prev) =>
        prev.map((msg) => {
          const msgId = msg._id?.toString() || msg.tempId?.toString();
          if (msgId === messageId.toString()) {
            return { ...msg, reactions: reactions || [] };
          }
          return msg;
        }),
      );
    });

    const unsubRemoved = onReactionRemoved((payload) => {
      if (!payload || !payload.messageId) return;
      const { messageId, reactions } = payload;

      // Update the message in the current view
      setMessages((prev) =>
        prev.map((msg) => {
          const msgId = msg._id?.toString() || msg.tempId?.toString();
          if (msgId === messageId.toString()) {
            return { ...msg, reactions: reactions || [] };
          }
          return msg;
        }),
      );
    });

    return () => {
      unsubAdded();
      unsubRemoved();
    };
  }, [onReactionAdded, onReactionRemoved]);

  // Real-time edit updates
  useEffect(() => {
    const unsub = onMessageEdited((payload) => {
      if (!payload || !payload.messageId) return;
      const { messageId, content, isEdited, editedAt } = payload;

      // Update the message in the current view
      setMessages((prev) =>
        prev.map((msg) => {
          const msgId = msg._id?.toString() || msg.tempId?.toString();
          if (msgId === messageId.toString()) {
            return { ...msg, content, isEdited, editedAt };
          }
          return msg;
        }),
      );

      // If we're editing this message, close edit mode
      if (editingMessage?._id?.toString() === messageId.toString()) {
        setEditingMessage(null);
      }
    });

    return () => unsub();
  }, [onMessageEdited, editingMessage]);

  // Real-time read receipt updates
  useEffect(() => {
    const unsub = onMessageRead((payload) => {
      if (!payload || !payload.messageId) return;
      const { messageId, readBy } = payload;

      // Update the message in the current view
      setMessages((prev) =>
        prev.map((msg) => {
          const msgId = msg._id?.toString() || msg.tempId?.toString();
          if (msgId === messageId.toString()) {
            return { ...msg, readBy };
          }
          return msg;
        }),
      );
    });

    return () => unsub();
  }, [onMessageRead]);

  // Scroll to bottom function
  const scrollToBottom = useCallback(
    (behavior = "smooth") => {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior });
        setIsAtBottom(true);
        setUnreadCount(0);
        if (messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          if (lastMessage?._id) {
            lastSeenMessageIdRef.current = lastMessage._id;
          }
        }
      }, 100);
    },
    [messages],
  );

  // Scroll detection: track if user is at bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Initial check
    checkIfAtBottom();

    const handleScroll = () => {
      checkIfAtBottom();
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    // Also check on resize
    window.addEventListener("resize", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [checkIfAtBottom]);

  // Check scroll position when messages change
  useEffect(() => {
    if (messages.length > 0 && !loadingMessages) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        checkIfAtBottom();
      }, 100);
    }
  }, [messages.length, loadingMessages, checkIfAtBottom]);

  // Scroll to bottom when messages finish loading on channel open
  useEffect(() => {
    if (!loadingMessages && selectedChannelId && messages.length > 0) {
      // Wait for messages to render, then scroll to bottom
      const scrollToBottom = () => {
        const container = messagesContainerRef.current;
        if (container && messagesEndRef.current) {
          // Use requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            messagesEndRef.current.scrollIntoView({ behavior: "auto" });
            setIsAtBottom(true);
            setUnreadCount(0);
            // Double-check after a short delay
            setTimeout(() => {
              if (messagesEndRef.current) {
                const container = messagesContainerRef.current;
                if (container) {
                  const scrollHeight = container.scrollHeight;
                  const clientHeight = container.clientHeight;
                  const scrollTop = container.scrollTop;
                  const distanceFromBottom =
                    scrollHeight - scrollTop - clientHeight;
                  // If not at bottom, scroll again
                  if (distanceFromBottom > 50) {
                    messagesEndRef.current.scrollIntoView({
                      behavior: "smooth",
                    });
                  }
                  setIsAtBottom(distanceFromBottom < 50);
                }
              }
            }, 100);
          });
        }
      };

      // Small delay to ensure DOM is updated
      setTimeout(scrollToBottom, 100);
    }
  }, [loadingMessages, selectedChannelId, messages.length]);

  // Auto-scroll when new messages arrive and user is at bottom
  useEffect(() => {
    if (
      !loadingMessages &&
      isAtBottom &&
      messages.length > 0 &&
      selectedChannelId
    ) {
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container && messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 50);
    }
  }, [messages.length, isAtBottom, loadingMessages, selectedChannelId]);

  // Track unread messages when scrolled up
  useEffect(() => {
    if (!selectedChannelId || messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage?._id) return;

    // If not at bottom and we have a new message
    if (!isAtBottom && lastMessage._id !== lastSeenMessageIdRef.current) {
      // Count messages after last seen
      const lastSeenIndex = messages.findIndex(
        (m) => m._id === lastSeenMessageIdRef.current,
      );
      const newMessagesCount =
        lastSeenIndex === -1
          ? messages.length
          : messages.length - lastSeenIndex - 1;
      setUnreadCount(newMessagesCount);
    }
  }, [messages, isAtBottom, selectedChannelId]);

  // Mark channel as read when entering (via socket for real-time receipt updates)
  useEffect(() => {
    if (!selectedChannelId || !isConnected || loadingMessages) return;
    const timer = setTimeout(() => {
      markChannelAsReadSocket(selectedChannelId).catch(() => {});
    }, 600);
    return () => clearTimeout(timer);
  }, [
    selectedChannelId,
    isConnected,
    loadingMessages,
    markChannelAsReadSocket,
  ]);

  // Real-time: when another user opens this channel, update all messages' readBy
  useEffect(() => {
    const unsub = onChannelRead((payload) => {
      if (!payload?.channelId) return;
      const { channelId, userId } = payload;
      if (String(channelId) !== String(selectedChannelId)) return;
      setMessages((prev) =>
        prev.map((msg) => {
          if (!msg._id) return msg;
          const alreadyRead = (msg.readBy || []).some(
            (r) => String(r.userId?._id || r.userId) === String(userId),
          );
          if (alreadyRead) return msg;
          const reader = channelMembers.find(
            (m) => String(m._id) === String(userId),
          );
          return {
            ...msg,
            readBy: [
              ...(msg.readBy || []),
              {
                userId: reader || { _id: userId },
                readAt: new Date().toISOString(),
              },
            ],
          };
        }),
      );
    });
    return unsub;
  }, [onChannelRead, selectedChannelId, channelMembers]);

  // Real-time: update a single message's readBy when any user marks it as seen
  useEffect(() => {
    const unsub = onMessageSeen((payload) => {
      if (!payload?.messageId) return;
      const { messageId, channelId, userId, readAt } = payload;
      if (String(channelId) !== String(selectedChannelId)) return;

      const msgIdStr = String(messageId);
      const readerIdStr = String(userId);

      // Check cache (always reflects current state, works even for optimistic temp messages)
      const cachedMsgs = getCachedMessages(selectedChannelId);
      const existsInCache = cachedMsgs.some(
        (m) => m._id && String(m._id) === msgIdStr,
      );

      if (!existsInCache) {
        // Message is still a pending temp — queue the seen event for after message:new
        const queue = pendingSeenEventsRef.current.get(msgIdStr) || [];
        queue.push({ userId, readAt });
        pendingSeenEventsRef.current.set(msgIdStr, queue);
        return;
      }

      // Build the new readBy entry (use populated member if available)
      const reader = channelMembers.find((m) => String(m._id) === readerIdStr);
      const newEntry = {
        userId: reader || { _id: userId },
        readAt: readAt || new Date().toISOString(),
      };

      // Update cache (union-merge, never overwrites)
      updateMessage(selectedChannelId, msgIdStr, { readBy: [newEntry] });

      // Update React state (same union-merge logic)
      setMessages((prev) =>
        prev.map((msg) => {
          if (!msg._id || String(msg._id) !== msgIdStr) return msg;
          const alreadyRead = (msg.readBy || []).some(
            (r) => String(r.userId?._id || r.userId) === readerIdStr,
          );
          if (alreadyRead) return msg;
          return { ...msg, readBy: [...(msg.readBy || []), newEntry] };
        }),
      );
    });
    return unsub;
  }, [
    onMessageSeen,
    selectedChannelId,
    channelMembers,
    getCachedMessages,
    updateMessage,
  ]);

  // Clear seen dedup cache and pending seen queue when switching channels
  useEffect(() => {
    seenMessageIdsRef.current = new Set();
    pendingSeenEventsRef.current = new Map();
  }, [selectedChannelId]);

  // Re-mark channel as read when browser tab becomes visible again
  useEffect(() => {
    if (!selectedChannelId || !isConnected) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        markChannelAsReadSocket(selectedChannelId).catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [selectedChannelId, isConnected, markChannelAsReadSocket]);

  // Cleanup rate limit timer on unmount
  useEffect(() => {
    return () => {
      if (rateLimitTimerRef.current) {
        clearTimeout(rateLimitTimerRef.current);
      }
    };
  }, []);

  // Close reaction picker on click outside
  useEffect(() => {
    if (!showReactionPicker) return;

    const handleClickOutside = (event) => {
      if (
        reactionPickerRef.current &&
        !reactionPickerRef.current.contains(event.target)
      ) {
        setShowReactionPicker(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showReactionPicker]);

  // Update rate limit countdown display and check expiration
  useEffect(() => {
    if (!isRateLimited || !rateLimitEndTime) return;

    const interval = setInterval(() => {
      const now = Date.now();
      if (now >= rateLimitEndTime) {
        setIsRateLimited(false);
        setRateLimitEndTime(null);
        messageTimestampsRef.current = [];
        localStorage.removeItem("chat_rateLimitEndTime");
        localStorage.removeItem("chat_messageTimestamps");
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isRateLimited, rateLimitEndTime]);

  // Check rate limit status when component mounts or channel changes
  useEffect(() => {
    if (rateLimitEndTime) {
      const now = Date.now();
      if (now >= rateLimitEndTime) {
        // Rate limit expired
        setIsRateLimited(false);
        setRateLimitEndTime(null);
        messageTimestampsRef.current = [];
        localStorage.removeItem("chat_rateLimitEndTime");
        localStorage.removeItem("chat_messageTimestamps");
        if (rateLimitTimerRef.current) {
          clearTimeout(rateLimitTimerRef.current);
          rateLimitTimerRef.current = null;
        }
      }
    }
  }, [selectedChannelId, rateLimitEndTime]);

  // Infinite scroll: load older messages when scrolling to top
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (
      !container ||
      !selectedChannelId ||
      loadingOlderMessages ||
      !hasMorePages
    )
      return;

    const handleScroll = () => {
      // Load more when scrolled to top (within 200px)
      if (container.scrollTop < 200 && !loadingOlderMessages && hasMorePages) {
        const oldestPage = getOldestPage(selectedChannelId);
        // oldestPage tracks the page number containing the oldest messages we've loaded
        // Page 1 = newest messages, Page 2 = older, Page 3 = even older, etc.
        const nextPage = oldestPage === null ? 2 : oldestPage + 1;

        // Check if page already loaded
        if (isPageLoaded(selectedChannelId, nextPage)) {
          return;
        }

        setLoadingOlderMessages(true);
        chatService
          .getChannelMessages(selectedChannelId, nextPage, MESSAGES_PER_PAGE)
          .then((msgs) => {
            if (msgs.length === 0) {
              setHasMorePages(false);
              return;
            }

            const messagesWithStatus = (Array.isArray(msgs) ? msgs : []).map(
              (msg) => ({
                ...msg,
                status: "delivered",
              }),
            );

            // Save current scroll position
            const scrollHeightBefore = container.scrollHeight;
            const scrollTopBefore = container.scrollTop;

            // Add messages to cache
            const updatedMessages = addMessages(
              selectedChannelId,
              messagesWithStatus,
              nextPage,
            );
            setMessages(updatedMessages);

            // Restore scroll position after DOM update
            setTimeout(() => {
              const scrollHeightAfter = container.scrollHeight;
              const scrollDiff = scrollHeightAfter - scrollHeightBefore;
              container.scrollTop = scrollTopBefore + scrollDiff;
            }, 0);

            // If we got fewer than MESSAGES_PER_PAGE, there are no more messages
            if (msgs.length < MESSAGES_PER_PAGE) {
              setHasMorePages(false);
            }
          })
          .catch((err) => {
            showError(
              err?.response?.data?.message || "Failed to load older messages",
            );
          })
          .finally(() => {
            setLoadingOlderMessages(false);
          });
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [
    selectedChannelId,
    loadingOlderMessages,
    hasMorePages,
    getOldestPage,
    isPageLoaded,
    addMessages,
    showError,
  ]);

  // Fetch employees for create-channel modal and add-members modal
  useEffect(() => {
    if (!isCreateChannelModalOpen && !isAddMembersModalOpen) return;
    setLoadingEmployees(true);
    axiosPrivate
      .get("/api/employees")
      .then((res) => {
        const data = res?.data;
        const list = Array.isArray(data)
          ? data
          : (data?.data ?? data?.employees ?? []);
        setEmployees(Array.isArray(list) ? list : []);
      })
      .catch(() => setEmployees([]))
      .finally(() => setLoadingEmployees(false));
  }, [isCreateChannelModalOpen, isAddMembersModalOpen]);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAttachmentFile(file);

    // Create preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setAttachmentPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };

  // Handle folder selection — shows confirmation modal before zipping
  const handleFolderChange = (e) => {
    const allFiles = Array.from(e.target.files || []);
    if (allFiles.length === 0) return;
    const folderName = (
      allFiles[0].webkitRelativePath?.split("/")[0] || "folder"
    ).replace(/[^a-zA-Z0-9._-]/g, "_");
    e.target.value = "";
    setFolderConfirmation({
      name: folderName,
      fileCount: allFiles.length,
      files: allFiles,
    });
  };

  const clearFolderFiles = () => setFolderConfirmation(null);

  const handleConfirmFolder = async () => {
    if (!folderConfirmation || !selectedChannelId) return;
    const { name: folderName, files: allFiles } = folderConfirmation;
    setFolderConfirmation(null);
    setFolderZipping(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder(folderName);
      for (const file of allFiles) {
        const relativePath = file.webkitRelativePath || file.name;
        const pathInZip =
          relativePath.split("/").slice(1).join("/") || file.name;
        folder.file(pathInZip, file);
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      setFolderZipping(false);
      setFolderUploading(true);
      const zipFileName = `${folderName}.zip`;
      const zipFile = new File([zipBlob], zipFileName, {
        type: "application/zip",
      });
      const path = `chat-files/${selectedChannelId}/${Date.now()}-${zipFileName}`;
      const { publicUrl, error: uploadError } = await uploadFile(
        IMAGES_BUCKET,
        path,
        zipFile,
      );
      if (uploadError) {
        showError(uploadError.message || "Upload failed");
        setFolderUploading(false);
        return;
      }
      await sendMessage(selectedChannelId, zipFileName, null, publicUrl, null);
      scrollToBottom("smooth");
    } catch (err) {
      showError("Failed to send folder");
    } finally {
      setFolderZipping(false);
      setFolderUploading(false);
    }
  };

  // Remove attachment
  const handleRemoveAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Rate limiting check
  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    const oneMinuteAgo = now - 60000; // 60 seconds

    // Check if already rate limited (from previous session or channel switch)
    if (rateLimitEndTime && now < rateLimitEndTime) {
      return true; // Still rate limited
    }

    // Remove timestamps older than 1 minute
    messageTimestampsRef.current = messageTimestampsRef.current.filter(
      (timestamp) => timestamp > oneMinuteAgo,
    );

    // Save updated timestamps to localStorage
    localStorage.setItem(
      "chat_messageTimestamps",
      JSON.stringify(messageTimestampsRef.current),
    );

    // Check if user has sent 20+ messages in the last minute
    if (messageTimestampsRef.current.length >= 20) {
      const blockUntil = now + 300000; // 5 minutes from now
      setIsRateLimited(true);
      setRateLimitEndTime(blockUntil);

      // Save to localStorage for persistence across channel switches
      localStorage.setItem("chat_rateLimitEndTime", blockUntil.toString());

      // Clear existing timer
      if (rateLimitTimerRef.current) {
        clearTimeout(rateLimitTimerRef.current);
      }

      // Set timer to unblock after 5 minutes
      rateLimitTimerRef.current = setTimeout(() => {
        setIsRateLimited(false);
        setRateLimitEndTime(null);
        messageTimestampsRef.current = [];
        localStorage.removeItem("chat_rateLimitEndTime");
        localStorage.removeItem("chat_messageTimestamps");
      }, 300000);

      return true; // Rate limited
    }

    return false; // Not rate limited
  }, [rateLimitEndTime]);

  // Filter channel members for @ mention (exclude current user, filter by query)
  const mentionCandidates = React.useMemo(() => {
    const currentIdStr = currentUserId?.toString?.();
    return channelMembers.filter((m) => {
      const id = m._id?.toString?.();
      if (id === currentIdStr) return false;
      const q = mentionQuery.trim().toLowerCase();
      if (!q) return true;
      const nickname = (m.nickname || m.fullNameEn || "").toLowerCase();
      const fullName = (m.fullNameEn || "").toLowerCase();
      return nickname.includes(q) || fullName.includes(q);
    });
  }, [channelMembers, currentUserId, mentionQuery]);

  useEffect(() => {
    setMentionSelectedIndex((i) => (i >= mentionCandidates.length ? 0 : i));
  }, [mentionCandidates.length]);

  const handleMessageInputChange = useCallback((e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart ?? value.length;
    setMessageInput(value);
    const lastAt = value.lastIndexOf("@");
    if (
      lastAt >= 0 &&
      cursor > lastAt &&
      !value.slice(lastAt, cursor).includes(" ")
    ) {
      setMentionStartIndex(lastAt);
      setMentionQuery(value.slice(lastAt + 1, cursor));
      setShowMentionDropdown(true);
      setMentionSelectedIndex(0);
    } else {
      setShowMentionDropdown(false);
    }
  }, []);

  const insertMention = useCallback(
    (member) => {
      const nickname = member.nickname || member.fullNameEn || "";
      const mentionText = nickname.includes(" ")
        ? `@${member.fullNameEn || member.nickname}`
        : `@${member.nickname || member.fullNameEn}`;
      const cursor =
        messageInputRef.current?.selectionStart ?? messageInput.length;
      const before = messageInput.slice(0, mentionStartIndex);
      const after = messageInput.slice(cursor);
      const newValue = before + mentionText + " " + after;
      setMessageInput(newValue);
      setShowMentionDropdown(false);
      setMentionQuery("");
      setTimeout(() => {
        messageInputRef.current?.focus();
        const newCursor = mentionStartIndex + mentionText.length + 1;
        messageInputRef.current?.setSelectionRange?.(newCursor, newCursor);
      }, 0);
    },
    [messageInput, mentionStartIndex],
  );

  const handleSendMessageRef = useRef(null);
  const isSendingRef = useRef(false); // guards against double-send on rapid Enter presses

  const handleMessageInputKeyDown = useCallback(
    (e) => {
      if (showMentionDropdown && mentionCandidates.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionSelectedIndex((i) => (i + 1) % mentionCandidates.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionSelectedIndex(
            (i) =>
              (i - 1 + mentionCandidates.length) % mentionCandidates.length,
          );
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          insertMention(mentionCandidates[mentionSelectedIndex]);
          return;
        }
        if (e.key === "Escape") {
          setShowMentionDropdown(false);
          return;
        }
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessageRef.current?.();
      }
    },
    [
      showMentionDropdown,
      mentionCandidates,
      mentionSelectedIndex,
      insertMention,
    ],
  );

  // Auto-resize textarea when messageInput changes
  useEffect(() => {
    const el = messageInputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
  }, [messageInput]);

  const handleSendMessage = async () => {
    // If in edit mode, save edit instead
    if (editingMessage) {
      await handleSaveEdit();
      return;
    }

    // Prevent double-send from rapid Enter presses or button double-click
    if (isSendingRef.current) return;

    const content = messageInput.trim();
    if (!content && !attachmentFile) return;
    if (!selectedChannelId) return;
    // Check rate limit
    if (checkRateLimit()) {
      const minutesLeft = Math.ceil((rateLimitEndTime - Date.now()) / 60000);
      showError(
        `Rate limit exceeded. You can send messages again in ${minutesLeft} minute${minutesLeft !== 1 ? "s" : ""}.`,
      );
      return;
    }

    // Add timestamp for rate limiting
    const timestamp = Date.now();
    messageTimestampsRef.current.push(timestamp);
    // Save to localStorage for persistence
    localStorage.setItem(
      "chat_messageTimestamps",
      JSON.stringify(messageTimestampsRef.current),
    );

    // Lock before any async work to block concurrent sends
    isSendingRef.current = true;

    // Prepare attachment — upload to Supabase and use URL (no base64, no size limit)
    let attachment = null;
    let attachmentUrlToSend = null;
    if (attachmentFile) {
      try {
        const safeFileName = attachmentFile.name.replace(
          /[^a-zA-Z0-9._-]/g,
          "_",
        );
        const path = `chat-files/${selectedChannelId}/${Date.now()}-${safeFileName}`;
        const { publicUrl, error: uploadError } = await uploadFile(
          IMAGES_BUCKET,
          path,
          attachmentFile,
        );
        if (uploadError) {
          showError(uploadError.message || "File upload failed");
          isSendingRef.current = false;
          return;
        }
        attachmentUrlToSend = publicUrl;
      } catch (err) {
        showError("Failed to upload file");
        isSendingRef.current = false;
        return;
      }
    }

    // Generate temp ID for optimistic message
    const tempId = `temp-${Date.now()}-${++pendingIdCounterRef.current}`;
    const optimisticMessage = {
      tempId,
      content: content || "",
      sender: {
        _id: currentUserId,
        fullNameEn: auth?.employee?.fullNameEn || "You",
      },
      createdAt: new Date().toISOString(),
      status: "pending",
      channel: selectedChannelId,
      replyTo: replyingTo?._id
        ? {
            _id: replyingTo._id,
            content: replyingTo.content,
            sender: replyingTo.sender,
          }
        : undefined,
      attachment: attachment
        ? {
            filename: attachment.filename,
            contentType: attachment.contentType,
            data: attachment.data, // Include base64 data for immediate display
            size: attachment.size,
          }
        : undefined,
      attachmentUrl: attachmentUrlToSend || undefined,
    };

    // Add optimistic message immediately (to cache and state)
    const updatedMessages = addNewMessage(selectedChannelId, optimisticMessage);
    setMessages(updatedMessages);

    // Track pending message
    setPendingMessages((prev) => {
      const newMap = new Map(prev);
      newMap.set(tempId, {
        tempId,
        content: content || attachmentFile?.name || "",
        senderId: currentUserId,
        timestamp: optimisticMessage.createdAt,
        attachment,
        attachmentUrl: attachmentUrlToSend || undefined,
      });
      return newMap;
    });

    // Play sent sound
    try {
      if (messageSentAudioRef.current) {
        messageSentAudioRef.current.currentTime = 0; // Reset to start
        messageSentAudioRef.current.play().catch((err) => {
          console.warn("Failed to play sent sound:", err);
        });
      }
    } catch (err) {
      console.warn("Error playing sent sound:", err);
    }

    // Clear input and attachment immediately (non-blocking)
    setMessageInput("");
    isSendingRef.current = false; // input cleared — safe to accept new messages
    setReplyingTo(null);
    handleRemoveAttachment();

    // Scroll to bottom when sending a message
    scrollToBottom("smooth");

    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 0);

    // Send to server in background (non-blocking)
    sendMessage(
      selectedChannelId,
      content || "",
      attachment,
      attachmentUrlToSend,
      replyingTo?._id,
    )
      .then((result) => {
        if (result?.error) {
          // Update message status to failed
          const updatedMessages = updateMessage(selectedChannelId, tempId, {
            status: "failed",
            error: result.error,
          });
          setMessages(updatedMessages);
          setPendingMessages((prev) => {
            const newMap = new Map(prev);
            newMap.delete(tempId);
            return newMap;
          });
          showError(result.error);
        } else {
          // Server confirmed - remove from pending (will be replaced by socket message)
          setPendingMessages((prev) => {
            const newMap = new Map(prev);
            newMap.delete(tempId);
            return newMap;
          });
          // Update status to delivered (socket will add the real message)
          const updatedMessages = updateMessage(selectedChannelId, tempId, {
            status: "delivered",
          });
          setMessages(updatedMessages);
        }
      })
      .catch((error) => {
        // Network error or other failure
        const updatedMessages = updateMessage(selectedChannelId, tempId, {
          status: "failed",
          error: error.message || "Failed to send",
        });
        setMessages(updatedMessages);
        setPendingMessages((prev) => {
          const newMap = new Map(prev);
          newMap.delete(tempId);
          return newMap;
        });
        showError(error.message || "Failed to send message");
      });
  };
  handleSendMessageRef.current = handleSendMessage;

  const handleRetryMessage = (tempId) => {
    const failedMsg = messages.find((m) => m.tempId === tempId);
    if (!failedMsg) return;
    // Remove failed message
    setMessages((prev) => prev.filter((msg) => msg.tempId !== tempId));
    setPendingMessages((prev) => {
      const newMap = new Map(prev);
      newMap.delete(tempId);
      return newMap;
    });
    // Retry sending with same content (will generate new tempId)
    const content = failedMsg.content;
    setMessageInput(content);
    setTimeout(() => {
      handleSendMessage(); // Will generate new tempId
    }, 0);
  };

  const handleDeleteMessage = async (messageId) => {
    if (!messageId || !selectedChannelId) return;

    try {
      const result = await deleteMessage(messageId, selectedChannelId);
      if (result.error) {
        showError(result.error);
      }
      // Message will be removed via socket 'message:deleted' event
      setDeleteMessageModal({ open: false, messageId: null });
    } catch (error) {
      showError("Failed to delete message");
    }
  };

  const handleReplyTo = (message) => {
    setReplyingTo({
      _id: message._id,
      content: message.content,
      sender: message.sender,
    });
    messageInputRef.current?.focus();
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const handleStartEdit = (message) => {
    setEditingMessage({
      _id: message._id,
      content: message.content,
    });
    setMessageInput(message.content);
    messageInputRef.current?.focus();
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setMessageInput("");
  };

  const handleSaveEdit = async () => {
    if (!editingMessage || !messageInput.trim()) return;

    try {
      const result = await editMessage(
        editingMessage._id,
        messageInput.trim(),
        selectedChannelId,
      );
      if (result.error) {
        showError(result.error);
      } else {
        setEditingMessage(null);
        setMessageInput("");
      }
    } catch (error) {
      showError("Failed to edit message");
    }
  };

  const handlePasteImage = async (event) => {
    try {
      const result = await uploadPastedImage(event);
      if (result) {
        const tempId = `temp-${Date.now()}-${++pendingIdCounterRef.current}`;
        const createdAt = new Date().toISOString();
        const optimisticMessage = {
          tempId,
          content: "",
          attachmentUrl: result.url,
          sender: {
            _id: currentUserId,
            fullNameEn: auth?.employee?.fullNameEn || "You",
            photo: auth?.employee?.photo || null,
          },
          createdAt,
          status: "pending",
          channel: selectedChannelId,
        };

        const updatedMessages = addNewMessage(
          selectedChannelId,
          optimisticMessage,
        );
        setMessages(updatedMessages);
        setPendingMessages((prev) => {
          const newMap = new Map(prev);
          newMap.set(tempId, {
            tempId,
            content: "",
            senderId: currentUserId,
            timestamp: createdAt,
            attachmentUrl: result.url,
          });
          return newMap;
        });

        const sendResult = await sendMessage(
          selectedChannelId,
          "",
          null,
          result.url,
          replyingTo?._id,
        );
        if (sendResult.error) {
          const updated = updateMessage(selectedChannelId, tempId, {
            status: "failed",
          });
          setMessages(updated);
          setPendingMessages((prev) => {
            const newMap = new Map(prev);
            newMap.delete(tempId);
            return newMap;
          });
          showError(sendResult.error);
        } else {
          // Server confirmed — remove from pending tracking;
          // socket message:new will replace the optimistic entry
          setPendingMessages((prev) => {
            const newMap = new Map(prev);
            newMap.delete(tempId);
            return newMap;
          });
          const updated = updateMessage(selectedChannelId, tempId, {
            status: "delivered",
          });
          setMessages(updated);
        }
        setReplyingTo(null);
      }
    } catch (error) {
      showError(error.message || "Failed to upload image");
    }
  };

  // Handle drag and drop image uploads
  const handleDropImage = useCallback(
    async (acceptedFiles) => {
      if (!selectedChannelId || acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];

      // Validate file
      const validation = validateFile(file, {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
      });

      if (!validation.valid) {
        showError(validation.error);
        return;
      }

      try {
        // Upload to Supabase first
        const result = await uploadToSupabase(file, "chat-images");

        // Create optimistic message using correct structure so reconciliation works
        const tempId = `temp-${Date.now()}-${++pendingIdCounterRef.current}`;
        const createdAt = new Date().toISOString();
        const optimisticMessage = {
          tempId,
          content: "",
          attachmentUrl: result.url,
          sender: {
            _id: currentUserId,
            fullNameEn: auth?.employee?.fullNameEn || "You",
            photo: auth?.employee?.photo || null,
          },
          createdAt,
          status: "pending",
          channel: selectedChannelId,
        };

        const updatedMessages = addNewMessage(
          selectedChannelId,
          optimisticMessage,
        );
        setMessages(updatedMessages);
        setPendingMessages((prev) => {
          const newMap = new Map(prev);
          newMap.set(tempId, {
            tempId,
            content: "",
            senderId: currentUserId,
            timestamp: createdAt,
            attachmentUrl: result.url,
          });
          return newMap;
        });

        // Emit socket message — server will persist and broadcast
        const sendResult = await sendMessage(
          selectedChannelId,
          "",
          null,
          result.url,
          replyingTo?._id,
        );
        if (sendResult.error) {
          const updated = updateMessage(selectedChannelId, tempId, {
            status: "failed",
          });
          setMessages(updated);
          setPendingMessages((prev) => {
            const newMap = new Map(prev);
            newMap.delete(tempId);
            return newMap;
          });
          showError(sendResult.error);
        } else {
          // Server confirmed — remove from pending tracking;
          // socket message:new will replace the optimistic entry
          setPendingMessages((prev) => {
            const newMap = new Map(prev);
            newMap.delete(tempId);
            return newMap;
          });
          const updated = updateMessage(selectedChannelId, tempId, {
            status: "delivered",
          });
          setMessages(updated);
        }
        setReplyingTo(null);
      } catch (error) {
        showError(error.message || "Failed to upload image");
      }
    },
    [
      selectedChannelId,
      currentUserId,
      auth.employee,
      sendMessage,
      addNewMessage,
      updateMessage,
      showError,
      replyingTo,
    ],
  );

  // Configure react-dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDropImage,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp"],
    },
    maxFiles: 1,
    noClick: true, // Only activate on drag, not click
    noKeyboard: true,
  });

  const handleAddReaction = async (messageId, emoji) => {
    if (!messageId || !emoji || !selectedChannelId) return;

    try {
      const result = await addReaction(messageId, emoji, selectedChannelId);
      if (result.error) {
        showError(result.error);
      }
      // Message reactions will be updated via socket 'message:reaction-added' event
      setShowReactionPicker(null); // Close picker after adding
    } catch (error) {
      showError("Failed to add reaction");
    }
  };

  const handleRemoveReaction = async (messageId, emoji) => {
    if (!messageId || !emoji || !selectedChannelId) return;

    try {
      const result = await removeReaction(messageId, emoji, selectedChannelId);
      if (result.error) {
        showError(result.error);
      }
      // Message reactions will be updated via socket 'message:reaction-removed' event
    } catch (error) {
      showError("Failed to remove reaction");
    }
  };

  const toggleMember = (employeeId) => {
    const id = employeeId?.toString?.() || employeeId;
    setNewChannel((prev) => {
      const has = (prev.memberIds || []).some((m) => m?.toString?.() === id);
      const memberIds = has
        ? (prev.memberIds || []).filter((m) => m?.toString?.() !== id)
        : [...(prev.memberIds || []), employeeId];
      return { ...prev, memberIds };
    });
  };

  const toggleTeam = (teamValue) => {
    setNewChannel((prev) => {
      const has = (prev.teamIds || []).includes(teamValue);
      const teamIds = has
        ? (prev.teamIds || []).filter((t) => t !== teamValue)
        : [...(prev.teamIds || []), teamValue];
      return { ...prev, teamIds };
    });
  };

  const removeMember = (employeeId) => {
    const id = employeeId?.toString?.();
    setNewChannel((prev) => ({
      ...prev,
      memberIds: (prev.memberIds || []).filter((m) => m?.toString?.() !== id),
    }));
  };

  const removeTeam = (teamValue) => {
    setNewChannel((prev) => ({
      ...prev,
      teamIds: (prev.teamIds || []).filter((t) => t !== teamValue),
    }));
  };

  const handleCreateChannel = async () => {
    const name = newChannel.name?.trim();
    const displayName = newChannel.displayName?.trim() || name;
    const hasMembers = (newChannel.memberIds?.length || 0) > 0;
    const hasTeams = (newChannel.teamIds?.length || 0) > 0;
    if (!name) {
      showError("Channel name is required");
      return;
    }
    if (!hasMembers && !hasTeams) {
      showError("Add at least one member or team");
      return;
    }
    setCreating(true);
    try {
      const channel = await chatService.createChannel({
        name,
        displayName,
        description: newChannel.description?.trim() || "",
        memberIds: newChannel.memberIds || [],
        teamIds: newChannel.teamIds || [],
      });
      setChannelsList((prev) => [channel, ...prev]);
      setSelectedChannelId(channel._id);
      setIsCreateChannelModalOpen(false);
      setNewChannel({
        name: "",
        displayName: "",
        description: "",
        memberIds: [],
        teamIds: [],
      });
      showSuccess("Channel created");
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to create channel");
    } finally {
      setCreating(false);
    }
  };

  const handleChannelSelect = (channelId) => {
    setSelectedChannelId(channelId);
    // Reset unread counter for this channel when it is opened
    if (!channelId) return;
    const channelIdStr = channelId?.toString?.() || String(channelId);
    setChannelUnreadCounts((prev) => {
      if (!prev[channelIdStr]) return prev;
      const next = { ...prev };
      delete next[channelIdStr];
      console.log("[GroupChat] Reset unread count for channel:", channelIdStr);
      return next;
    });
  };

  const handleDeleteChannel = async () => {
    const channelId = deleteConfirmModal.channelId;
    if (!channelId) return;
    setDeleting(true);
    try {
      await chatService.deleteChannel(channelId);
      // Leave the socket room for deleted channel
      leaveChannel(channelId);
      setChannelsList((prev) => prev.filter((ch) => ch._id !== channelId));
      if (selectedChannelId === channelId) {
        setSelectedChannelId(null);
        setMessages([]);
        setChannelMembers([]);
      }
      clearCache(channelId);
      setDeleteConfirmModal({ open: false, channelId: null });
      showSuccess("Channel deleted successfully");
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to delete channel");
    } finally {
      setDeleting(false);
    }
  };

  const handleAddMembersToChannel = async () => {
    if (!selectedChannelId || selectedMembersToAdd.length === 0) return;
    setAddingMembers(true);
    try {
      await chatService.addMembers(selectedChannelId, selectedMembersToAdd);
      // Refresh channel members
      const members = await chatService.getChannelMembers(selectedChannelId);
      // Sort members alphabetically by name (A-Z)
      const sortedMembers = Array.isArray(members)
        ? members.sort((a, b) => {
            const nameA = (a.fullNameEn || "").toLowerCase();
            const nameB = (b.fullNameEn || "").toLowerCase();
            return nameA.localeCompare(nameB);
          })
        : [];
      setChannelMembers(sortedMembers);
      setIsAddMembersModalOpen(false);
      setSelectedMembersToAdd([]);
      showSuccess("Members added successfully");
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to add members");
    } finally {
      setAddingMembers(false);
    }
  };

  const toggleMemberToAdd = (employeeId) => {
    const id = employeeId?.toString?.() || employeeId;
    setSelectedMembersToAdd((prev) => {
      const has = prev.some((m) => m?.toString?.() === id);
      if (has) {
        return prev.filter((m) => m?.toString?.() !== id);
      }
      return [...prev, employeeId];
    });
  };

  const formatMessageTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Only show online when socket is connected and member ID is in the presence list (logged-in users only)
  const isMemberOnline = (memberId) => {
    if (!isConnected || !memberId) return false;
    if (!Array.isArray(onlineUserIds) || onlineUserIds.length === 0)
      return false;
    const memberIdStr = String(memberId);
    return onlineUserIds.some((id) => id != null && String(id) === memberIdStr);
  };

  // Filter channels based on search query
  const filteredChannels = channelsList.filter((channel) => {
    if (!channelSearchQuery.trim()) return true;
    const query = channelSearchQuery.toLowerCase();
    const name = (channel.name || "").toLowerCase();
    const displayName = (channel.displayName || "").toLowerCase();
    return name.includes(query) || displayName.includes(query);
  });

  return (
    <PageWrapper
      title="Group Chat"
      subtitle="Team communication"
      actions={
        <div className="flex items-center gap-3">
          <Button onClick={() => setIsCreateChannelModalOpen(true)}>
            <Plus className="w-4 h-4" />
            Create Channel
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-6 overflow-hidden">
        {/* Channels Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-light-100">
              Channels
            </h3>
            <button
              onClick={() => setIsCreateChannelModalOpen(true)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-200 rounded-lg transition-colors"
              title="Create Channel"
            >
              <Plus className="w-4 h-4 text-gray-500 dark:text-light-400 hover:text-primary-accent" />
            </button>
          </div>

          {/* Channel Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={channelSearchQuery}
                onChange={(e) => setChannelSearchQuery(e.target.value)}
                placeholder="Search channels..."
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-dark-200 bg-white dark:bg-dark-100 text-gray-900 dark:text-light-100 placeholder-gray-400 dark:placeholder-light-500 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all"
              />
            </div>
          </div>
          {loadingChannels ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary-accent" />
            </div>
          ) : (
            <div className="space-y-1 max-h-[calc(100vh-420px)] overflow-y-auto scrollbar-thin">
              {channelsList.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-light-500 py-4">
                  No channels yet. Create one to get started.
                </p>
              ) : filteredChannels.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-light-500 py-4">
                  No channels match your search.
                </p>
              ) : (
                filteredChannels.map((channel) => {
                  const channelIdStr =
                    channel._id?.toString?.() || String(channel._id);
                  const unreadForChannel =
                    channelUnreadCounts[channelIdStr] || 0;
                  const isCreator =
                    channel.createdBy?._id?.toString() ===
                      currentUserId?.toString() ||
                    channel.createdBy?.toString() === currentUserId?.toString();
                  return (
                    <div
                      key={channel._id}
                      className={`w-full rounded-lg transition-all flex items-center gap-1 group ${
                        selectedChannelId === channel._id
                          ? "bg-primary-accent/20 border-l-4 border-l-primary-accent"
                          : ""
                      }`}
                    >
                      <button
                        onClick={() => handleChannelSelect(channel._id)}
                        className={`flex-1 text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-2 min-w-0 ${
                          selectedChannelId === channel._id
                            ? "text-primary-accent"
                            : "hover:bg-gray-100 dark:hover:bg-dark-200 text-gray-700 dark:text-light-300"
                        }`}
                      >
                        <Hash className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm font-medium truncate flex-1">
                          {channel.displayName || channel.name}
                        </span>
                        {unreadForChannel > 0 &&
                          selectedChannelId !== channel._id && (
                            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-accent text-white flex-shrink-0">
                              {unreadForChannel}
                            </span>
                          )}
                      </button>
                      {isCreator && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmModal({
                              open: true,
                              channelId: channel._id,
                            });
                          }}
                          className="p-2 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 hover:text-red-500 rounded-lg transition-all"
                          title="Delete channel"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </motion.div>

        {/* Chat Area */}
        <div className="lg:col-span-3 min-h-[300px] md:min-h-[400px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card flex flex-col h-[calc(100vh-200px)] sm:h-[calc(100vh-220px)] lg:h-[calc(100vh-240px)] min-h-[280px]"
          >
            {!selectedChannelId ? (
              <div className="flex-1 flex flex-col items-center justify-center h-full px-6 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gray-100 dark:bg-dark-100 flex items-center justify-center mb-4 text-gray-400 dark:text-light-500">
                  <Hash className="w-10 h-10" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-light-300 mb-1">
                  No channel selected
                </h3>
                <p className="text-sm text-gray-500 dark:text-light-500 max-w-xs">
                  Select a channel from the list to start chatting
                </p>
              </div>
            ) : (
              <>
                <div className="pb-4 border-b border-gray-200 dark:border-dark-100">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Hash className="w-5 h-5 text-gray-500 dark:text-light-400" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-light-100">
                        {selectedChannel?.displayName ||
                          selectedChannel?.name ||
                          "Channel"}
                      </h3>
                      {isConnected && (
                        <span
                          className="w-2 h-2 rounded-full bg-green-500"
                          title="Connected"
                        />
                      )}
                    </div>
                    {selectedChannel?.createdBy?._id?.toString() ===
                      currentUserId?.toString() ||
                    selectedChannel?.createdBy?.toString() ===
                      currentUserId?.toString() ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsAddMembersModalOpen(true)}
                        className="text-xs"
                      >
                        <Plus className="w-3 h-3" />
                        Add Members
                      </Button>
                    ) : null}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-light-500">
                    {selectedChannel?.description || "Team communication"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-light-500 mt-1">
                    {channelMembers.length} member
                    {channelMembers.length !== 1 ? "s" : ""}
                  </p>
                </div>

                <div
                  className="flex-1 relative overflow-hidden"
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />

                  {/* Drag and Drop Overlay */}
                  {isDragActive && (
                    <div className="absolute inset-0 z-50 bg-primary-accent/10 backdrop-blur-sm border-4 border-dashed border-primary-accent rounded-lg flex items-center justify-center pointer-events-none">
                      <div className="bg-white dark:bg-dark-100 rounded-2xl shadow-2xl px-8 py-6 text-center">
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-accent/20 flex items-center justify-center">
                            <svg
                              className="w-8 h-8 text-primary-accent"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              />
                            </svg>
                          </div>
                          <p className="text-lg font-semibold text-gray-900 dark:text-light-100 mb-1">
                            Drop your image here
                          </p>
                          <p className="text-sm text-gray-500 dark:text-light-500">
                            Supports PNG, JPG, GIF, WebP (max 10MB)
                          </p>
                        </motion.div>
                      </div>
                    </div>
                  )}

                  <div
                    ref={messagesContainerRef}
                    className="h-full overflow-y-auto py-4 space-y-4 scrollbar-thin"
                  >
                    {loadingOlderMessages && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-primary-accent" />
                        <span className="ml-2 text-sm text-gray-500 dark:text-light-500">
                          Loading older messages...
                        </span>
                      </div>
                    )}
                    {messages.length === 0 ? (
                      <div className="flex items-center justify-center py-8 text-gray-500 dark:text-light-500">
                        No messages yet. Start the conversation!
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const status =
                          msg.status || (msg.tempId ? "pending" : "delivered");
                        const isOwnMessage =
                          msg.sender?._id?.toString() ===
                            currentUserId?.toString() || msg.tempId; // Optimistic messages are always own
                        return (
                          <motion.div
                            key={msg._id || msg.tempId}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex gap-3 ${isOwnMessage ? "flex-row-reverse" : ""}`}
                          >
                            <Avatar
                              src={msg.sender?.photo}
                              alt={msg.sender?.fullNameEn}
                              size="md"
                              name={msg.sender?.fullNameEn}
                            />
                            <div
                              className={`flex flex-col ${isOwnMessage ? "items-end" : "items-start"}`}
                            >
                              <div
                                className={`flex items-center gap-2 mb-1 ${isOwnMessage ? "flex-row-reverse" : ""}`}
                              >
                                <span className="font-medium text-gray-900 dark:text-light-100 text-sm">
                                  {msg.sender?.fullNameEn || "Unknown"}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-light-500">
                                  {formatMessageTime(msg.createdAt)}
                                </span>
                                {/* Message Status Indicator */}
                                {isOwnMessage && (
                                  <div className="flex items-center gap-1">
                                    {status === "pending" && (
                                      <Clock
                                        className="w-3 h-3 text-yellow-500"
                                        title="Pending"
                                      />
                                    )}
                                    {status === "delivered" && (
                                      <CheckCircle2
                                        className="w-3 h-3 text-green-500"
                                        title="Delivered"
                                      />
                                    )}
                                    {status === "failed" && (
                                      <div className="flex items-center gap-1">
                                        <AlertCircle
                                          className="w-3 h-3 text-red-500"
                                          title="Failed"
                                        />
                                        <button
                                          onClick={() =>
                                            handleRetryMessage(msg.tempId)
                                          }
                                          className="p-0.5 hover:bg-red-500/20 rounded transition-colors"
                                          title="Retry"
                                        >
                                          <RotateCcw className="w-3 h-3 text-red-500" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {/* Action Buttons - Reply + Info (edit/delete/seen) */}
                                {status === "delivered" && msg._id && (
                                  <div className="flex items-center gap-1">
                                    {/* Reply Button - for all messages */}
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={() => handleReplyTo(msg)}
                                      className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 dark:bg-dark-300 border border-gray-200/60 dark:border-dark-200/60 text-gray-500 dark:text-light-400 hover:bg-blue-500/15 hover:border-blue-400/60 hover:text-blue-500 transition-all duration-200"
                                      title="Reply to message"
                                    >
                                      <Reply className="w-3 h-3" />
                                    </motion.button>
                                    {/* Info Button - only for own messages */}
                                    {isOwnMessage &&
                                      (() => {
                                        const isOneOnOne =
                                          channelMembers.length === 2;
                                        const readByOthers = (
                                          msg.readBy || []
                                        ).filter(
                                          (r) =>
                                            String(
                                              r.userId?._id || r.userId,
                                            ) !== String(currentUserId),
                                        );
                                        return (
                                          <div className="relative">
                                            <motion.button
                                              whileHover={{ scale: 1.1 }}
                                              whileTap={{ scale: 0.9 }}
                                              onMouseDown={(e) =>
                                                e.stopPropagation()
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setReadReceiptPopover(
                                                  readReceiptPopover === msg._id
                                                    ? null
                                                    : msg._id,
                                                );
                                              }}
                                              className={`w-5 h-5 flex items-center justify-center rounded-full border text-[10px] font-bold transition-all duration-200 ${
                                                readReceiptPopover === msg._id
                                                  ? "bg-primary-accent border-primary-accent text-white shadow-md"
                                                  : "bg-gray-100 dark:bg-dark-300 border-gray-200/60 dark:border-dark-200/60 text-gray-500 dark:text-light-400 hover:bg-blue-500/15 hover:border-blue-400/60 hover:text-blue-500"
                                              }`}
                                              title="Message info"
                                            >
                                              i
                                            </motion.button>

                                            {readReceiptPopover === msg._id && (
                                              <div
                                                className="absolute top-full right-0 mt-2 z-50 bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-xl shadow-xl overflow-hidden min-w-[200px] max-w-[250px]"
                                                onMouseDown={(e) =>
                                                  e.stopPropagation()
                                                }
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                {/* Edit option */}
                                                <button
                                                  onClick={() => {
                                                    handleStartEdit(msg);
                                                    setReadReceiptPopover(null);
                                                  }}
                                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 dark:text-light-200 hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors"
                                                >
                                                  <Edit2 className="w-3.5 h-3.5 text-yellow-500" />
                                                  Edit message
                                                </button>

                                                {/* Delete option */}
                                                <button
                                                  onClick={() => {
                                                    setDeleteMessageModal({
                                                      open: true,
                                                      messageId: msg._id,
                                                    });
                                                    setReadReceiptPopover(null);
                                                  }}
                                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                                  Delete message
                                                </button>

                                                {/* View seen by — opens modal (group only) */}
                                                {!isOneOnOne && (
                                                  <>
                                                    <div className="border-t border-gray-100 dark:border-dark-300" />
                                                    <button
                                                      onClick={() => {
                                                        setSeenModal({
                                                          open: true,
                                                          msg,
                                                        });
                                                        setReadReceiptPopover(
                                                          null,
                                                        );
                                                      }}
                                                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 dark:text-light-200 hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors"
                                                    >
                                                      <Users className="w-3.5 h-3.5 text-blue-400" />
                                                      View seen by
                                                      {readByOthers.length >
                                                        0 && (
                                                        <span className="ml-auto text-xs font-semibold text-primary-accent">
                                                          {readByOthers.length}
                                                        </span>
                                                      )}
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                  </div>
                                )}

                                {/* Add Reaction Button - beside time for all delivered messages */}
                                {status === "delivered" && msg._id && (
                                  <div className="relative">
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.95 }}
                                      onClick={() =>
                                        setShowReactionPicker(
                                          showReactionPicker === msg._id
                                            ? null
                                            : msg._id,
                                        )
                                      }
                                      className={`w-5 h-5 flex items-center justify-center rounded-full border text-xs font-bold transition-all duration-200 ${
                                        showReactionPicker === msg._id
                                          ? "bg-primary-accent border-primary-accent text-white shadow-md"
                                          : "bg-gray-100 dark:bg-dark-300 border-gray-200/60 dark:border-dark-200/60 text-gray-500 dark:text-light-400 hover:bg-blue-500/15 hover:border-blue-400/60 hover:text-blue-500"
                                      }`}
                                      title="Add reaction"
                                    >
                                      {showReactionPicker === msg._id
                                        ? "✕"
                                        : "+"}
                                    </motion.button>

                                    {/* Emoji Picker with Animation */}
                                    {showReactionPicker === msg._id && (
                                      <>
                                        {/* Backdrop */}
                                        <div
                                          className="fixed inset-0 z-40"
                                          onClick={() =>
                                            setShowReactionPicker(null)
                                          }
                                        />
                                        <motion.div
                                          ref={reactionPickerRef}
                                          initial={{
                                            opacity: 0,
                                            scale: 0.9,
                                            y: 10,
                                          }}
                                          animate={{
                                            opacity: 1,
                                            scale: 1,
                                            y: 0,
                                          }}
                                          exit={{
                                            opacity: 0,
                                            scale: 0.9,
                                            y: 10,
                                          }}
                                          transition={{ duration: 0.15 }}
                                          className={`absolute ${
                                            isOwnMessage ? "right-0" : "left-0"
                                          } top-full mt-2 z-50 bg-white dark:bg-primary-card border border-gray-200 dark:border-dark-100 rounded-xl shadow-xl p-2 flex gap-1`}
                                        >
                                          {[
                                            "👍",
                                            "❤️",
                                            "😂",
                                            "😮",
                                            "😢",
                                            "🎉",
                                            "🔥",
                                            "👏",
                                          ].map((emoji, idx) => (
                                            <motion.button
                                              key={emoji}
                                              initial={{ opacity: 0, scale: 0 }}
                                              animate={{ opacity: 1, scale: 1 }}
                                              transition={{ delay: idx * 0.03 }}
                                              whileHover={{
                                                scale: 1.3,
                                                rotate: [0, -10, 10, -10, 0],
                                                transition: { duration: 0.3 },
                                              }}
                                              whileTap={{ scale: 0.9 }}
                                              onClick={() =>
                                                handleAddReaction(
                                                  msg._id,
                                                  emoji,
                                                )
                                              }
                                              className="text-2xl p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-300 transition-colors"
                                              title={emoji}
                                            >
                                              {emoji}
                                            </motion.button>
                                          ))}
                                        </motion.div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div
                                className={`max-w-[75%] min-w-0 overflow-hidden p-3 rounded-2xl ${
                                  isOwnMessage
                                    ? "bg-gradient-to-br from-primary-accent to-primary-accent/90 text-white rounded-tr-sm"
                                    : "bg-gray-200 dark:bg-dark-300 text-gray-900 dark:text-light-200 rounded-tl-sm"
                                } ${status === "failed" ? "opacity-75" : ""}`}
                              >
                                {/* Reply preview if this is a reply */}
                                {msg.replyTo && (
                                  <div
                                    className={`mb-2 pb-2 border-b ${
                                      isOwnMessage
                                        ? "border-white/20"
                                        : "border-gray-400/30 dark:border-dark-200"
                                    }`}
                                  >
                                    <p
                                      className={`text-xs font-medium ${
                                        isOwnMessage
                                          ? "text-white/80"
                                          : "text-gray-600 dark:text-light-400"
                                      }`}
                                    >
                                      Replying to{" "}
                                      {msg.replyTo.sender?.fullNameEn ||
                                        "Unknown"}
                                    </p>
                                    <p
                                      {...getTextDirectionStyle(
                                        msg.replyTo.content || "",
                                      )}
                                      className={`text-xs italic line-clamp-2 ${
                                        isOwnMessage
                                          ? "text-white/70"
                                          : "text-gray-500 dark:text-light-500"
                                      }`}
                                    >
                                      {msg.replyTo.content || "[Attachment]"}
                                    </p>
                                  </div>
                                )}
                                {msg.content && (
                                  <p
                                    {...getTextDirectionStyle(msg.content)}
                                    className={`text-sm whitespace-pre-wrap break-all ${
                                      isOwnMessage
                                        ? "text-white"
                                        : "text-gray-900 dark:text-light-200"
                                    }`}
                                  >
                                    {renderContentWithMentions(
                                      msg.content,
                                      isOwnMessage,
                                    )}
                                  </p>
                                )}
                                {/* Edited badge */}
                                {msg.isEdited && (
                                  <span
                                    className={`block mt-1 text-[10px] italic ${
                                      isOwnMessage
                                        ? "text-white/60"
                                        : "text-gray-500 dark:text-light-500"
                                    }`}
                                  >
                                    (Edited)
                                  </span>
                                )}
                                {msg.attachment && msg.attachment.data && (
                                  <div className="mt-2">
                                    {msg.attachment.contentType?.startsWith(
                                      "image/",
                                    ) ? (
                                      <div className="rounded-lg overflow-hidden max-w-xs">
                                        <img
                                          src={`data:${msg.attachment.contentType};base64,${msg.attachment.data}`}
                                          alt={msg.attachment.filename}
                                          className="max-w-full h-auto cursor-pointer hover:opacity-90"
                                          onClick={() => {
                                            const img = new Image();
                                            img.src = `data:${msg.attachment.contentType};base64,${msg.attachment.data}`;
                                            const w = window.open("");
                                            w.document.write(
                                              `<img src="${img.src}" style="max-width:100%;height:auto;" />`,
                                            );
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <a
                                        href={`data:${msg.attachment.contentType};base64,${msg.attachment.data}`}
                                        download={msg.attachment.filename}
                                        className={`flex items-center gap-2 p-2 rounded-lg hover:bg-opacity-80 transition-colors ${
                                          isOwnMessage
                                            ? "bg-white/20 text-white"
                                            : "bg-gray-300 dark:bg-dark-300 text-gray-900 dark:text-light-200"
                                        }`}
                                      >
                                        <FileText className="w-4 h-4" />
                                        <span className="text-sm truncate flex-1">
                                          {msg.attachment.filename}
                                        </span>
                                        <Download className="w-4 h-4 flex-shrink-0" />
                                        {msg.attachment.size && (
                                          <span className="text-xs opacity-75">
                                            (
                                            {(
                                              msg.attachment.size / 1024
                                            ).toFixed(1)}{" "}
                                            KB)
                                          </span>
                                        )}
                                      </a>
                                    )}
                                  </div>
                                )}
                                {/* Supabase attachmentUrl — images shown inline, other files as download link */}
                                {msg.attachmentUrl &&
                                  (() => {
                                    const url = msg.attachmentUrl;
                                    const isImage =
                                      /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(
                                        url,
                                      );
                                    const displayName =
                                      decodeURIComponent(
                                        url.split("/").pop() || "file",
                                      ).replace(/^\d+-/, "") || "file";
                                    return isImage ? (
                                      <div className="mt-2">
                                        <div className="rounded-lg overflow-hidden max-w-xs">
                                          <img
                                            src={url}
                                            alt="Attachment"
                                            className="max-w-full h-auto cursor-pointer hover:opacity-90"
                                            onClick={() => {
                                              window.open(url, "_blank");
                                            }}
                                          />
                                        </div>
                                      </div>
                                    ) : (
                                      <a
                                        href={url}
                                        download={displayName}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`mt-2 flex items-center gap-2 p-2 rounded-lg hover:bg-opacity-80 transition-colors ${
                                          isOwnMessage
                                            ? "bg-white/20 text-white"
                                            : "bg-gray-300 dark:bg-dark-300 text-gray-900 dark:text-light-200"
                                        }`}
                                      >
                                        <FileText className="w-4 h-4" />
                                        <span className="text-sm truncate flex-1">
                                          {displayName}
                                        </span>
                                        <Download className="w-4 h-4 flex-shrink-0" />
                                      </a>
                                    );
                                  })()}
                              </div>

                              {/* Reactions Display - shown below message */}
                              {msg.reactions && msg.reactions.length > 0 && (
                                <div
                                  className={`mt-2 ${isOwnMessage ? "flex justify-end" : ""}`}
                                >
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    {(() => {
                                      // Group reactions by emoji
                                      const reactionMap = {};
                                      msg.reactions.forEach((r) => {
                                        if (!reactionMap[r.emoji]) {
                                          reactionMap[r.emoji] = [];
                                        }
                                        reactionMap[r.emoji].push(r.userId);
                                      });

                                      return Object.entries(reactionMap).map(
                                        ([emoji, userIds]) => {
                                          const userReacted = userIds.some(
                                            (uid) =>
                                              String(uid) ===
                                              String(currentUserId),
                                          );

                                          // Get names of users who reacted
                                          const reactedMemberNames = userIds
                                            .map((uid) => {
                                              const member =
                                                channelMembers.find(
                                                  (m) =>
                                                    String(m._id) ===
                                                    String(uid),
                                                );
                                              if (
                                                String(uid) ===
                                                String(currentUserId)
                                              ) {
                                                return "You";
                                              }
                                              return (
                                                member?.fullNameEn || "Unknown"
                                              );
                                            })
                                            .slice(0, 10); // Limit to 10 names

                                          const tooltipText =
                                            reactedMemberNames.length > 5
                                              ? `${reactedMemberNames.slice(0, 5).join(", ")} and ${reactedMemberNames.length - 5} others`
                                              : reactedMemberNames.join(", ");

                                          return (
                                            <motion.button
                                              key={emoji}
                                              initial={{ scale: 0 }}
                                              animate={{ scale: 1 }}
                                              whileHover={{ scale: 1.1 }}
                                              whileTap={{ scale: 0.95 }}
                                              onClick={() =>
                                                userReacted
                                                  ? handleRemoveReaction(
                                                      msg._id,
                                                      emoji,
                                                    )
                                                  : handleAddReaction(
                                                      msg._id,
                                                      emoji,
                                                    )
                                              }
                                              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                                                userReacted
                                                  ? "bg-primary-accent/15 border-2 border-primary-accent text-primary-accent shadow-sm"
                                                  : "bg-white/80 dark:bg-dark-300/80 backdrop-blur-sm border border-gray-300/50 dark:border-dark-200/50 text-gray-700 dark:text-light-300 hover:bg-gray-100 dark:hover:bg-dark-200 hover:shadow-sm"
                                              }`}
                                              title={tooltipText}
                                            >
                                              <span className="text-base leading-none">
                                                {emoji}
                                              </span>
                                              <span
                                                className={`text-xs font-semibold ${userReacted ? "text-primary-accent" : "text-gray-600 dark:text-light-400"}`}
                                              >
                                                {userIds.length}
                                              </span>
                                            </motion.button>
                                          );
                                        },
                                      );
                                    })()}
                                  </div>
                                </div>
                              )}
                              {/* 1-on-1 Seen / Delivered label */}
                              {isOwnMessage &&
                                channelMembers.length === 2 &&
                                status === "delivered" &&
                                (() => {
                                  const isSeen = (msg.readBy || []).some(
                                    (r) =>
                                      String(r.userId?._id || r.userId) !==
                                      String(currentUserId),
                                  );
                                  return (
                                    <p className="text-[10px] mt-0.5 text-right text-gray-400 dark:text-light-500">
                                      {isSeen ? "Seen" : "Delivered"}
                                    </p>
                                  );
                                })()}
                            </div>
                          </motion.div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Go Down Button - shows when user is scrolled up */}
                  {!isAtBottom && selectedChannelId && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
                      <motion.button
                        initial={{ opacity: 0, y: 10, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.9 }}
                        onClick={() => scrollToBottom("smooth")}
                        className="flex items-center gap-2 px-4 py-2 bg-primary-accent hover:bg-primary-accent/90 text-white rounded-full shadow-lg transition-all hover:shadow-xl pointer-events-auto"
                      >
                        {unreadCount > 0 ? (
                          <>
                            <span className="text-sm font-medium">
                              {unreadCount} New Message
                              {unreadCount !== 1 ? "s" : ""}
                            </span>
                            <ArrowDown className="w-4 h-4" />
                          </>
                        ) : (
                          <>
                            <span className="text-sm font-medium">Go Down</span>
                            <ArrowDown className="w-4 h-4" />
                          </>
                        )}
                      </motion.button>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-dark-100">
                  {/* Reply Preview Bar */}
                  {replyingTo && (
                    <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-lg">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Reply className="w-4 h-4 text-blue-500" />
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                              Replying to{" "}
                              {replyingTo.sender?.fullNameEn || "Unknown"}
                            </span>
                          </div>
                          <p
                            {...getTextDirectionStyle(replyingTo.content || "")}
                            className="text-sm text-gray-600 dark:text-light-400 line-clamp-2"
                          >
                            {replyingTo.content || "[Attachment]"}
                          </p>
                        </div>
                        <button
                          onClick={handleCancelReply}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-dark-300 rounded transition-colors flex-shrink-0"
                          title="Cancel reply"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Edit Mode Indicator */}
                  {editingMessage && (
                    <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 rounded-lg">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Edit2 className="w-4 h-4 text-yellow-500" />
                          <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                            Editing message
                          </span>
                        </div>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-dark-300 rounded transition-colors"
                          title="Cancel edit"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Rate Limit Warning */}
                  {isRateLimited && rateLimitEndTime && (
                    <div className="mb-3 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
                      <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-400">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Rate limit exceeded. You can send messages again in{" "}
                          {Math.ceil((rateLimitEndTime - Date.now()) / 60000)}{" "}
                          minute
                          {Math.ceil(
                            (rateLimitEndTime - Date.now()) / 60000,
                          ) !== 1
                            ? "s"
                            : ""}
                          .
                        </span>
                      </div>
                    </div>
                  )}
                  {/* Attachment Preview */}
                  {attachmentFile && (
                    <div className="mb-3 flex items-center gap-2 p-2 bg-gray-100 dark:bg-dark-400 rounded-lg">
                      {attachmentPreview ? (
                        <img
                          src={attachmentPreview}
                          alt="Preview"
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : (
                        <FileText className="w-8 h-8 text-primary-accent" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 dark:text-light-200 truncate">
                          {attachmentFile.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-light-500">
                          {(attachmentFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        onClick={handleRemoveAttachment}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-dark-300 rounded transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-500 dark:text-light-400" />
                      </button>
                    </div>
                  )}
                  {/* Folder progress chip */}
                  {(folderZipping || folderUploading) && (
                    <div className="mb-2 flex items-center gap-2">
                      <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-medium border border-emerald-500/20">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        {folderZipping
                          ? "Zipping folder..."
                          : "Uploading zip..."}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2 items-end">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.zip"
                      className="hidden"
                      id="chat-file-input"
                    />
                    {/* Hidden folder picker — CSV only */}
                    <input
                      type="file"
                      ref={folderInputRef}
                      // @ts-ignore
                      webkitdirectory=""
                      onChange={handleFolderChange}
                      className="hidden"
                      id="chat-folder-input"
                    />
                    <label
                      htmlFor="chat-file-input"
                      className="flex items-center justify-center px-3 py-2.5 bg-gray-100 dark:bg-dark-400 hover:bg-gray-200 dark:hover:bg-dark-300 rounded-lg cursor-pointer transition-colors"
                      title="Attach file (uploaded to Supabase, any size)"
                    >
                      <Paperclip className="w-5 h-5 text-gray-500 dark:text-light-400" />
                    </label>
                    <label
                      htmlFor="chat-folder-input"
                      className="flex items-center justify-center px-3 py-2.5 bg-gray-100 dark:bg-dark-400 hover:bg-gray-200 dark:hover:bg-dark-300 rounded-lg cursor-pointer transition-colors"
                      title="Upload CSV folder"
                    >
                      <FolderOpen className="w-5 h-5 text-gray-500 dark:text-light-400" />
                    </label>
                    <div ref={mentionDropdownRef} className="flex-1 relative">
                      <textarea
                        ref={messageInputRef}
                        rows={1}
                        placeholder={
                          editingMessage
                            ? "Edit your message..."
                            : replyingTo
                              ? "Type your reply..."
                              : "Type a message... Use @ to mention • Paste or drag & drop images"
                        }
                        value={messageInput}
                        onChange={handleMessageInputChange}
                        onKeyDown={handleMessageInputKeyDown}
                        onPaste={handlePasteImage}
                        className="w-full input-field px-4 py-2.5 rounded-lg bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent resize-none overflow-hidden leading-relaxed"
                        style={{ minHeight: "44px", maxHeight: "150px" }}
                      />
                      {showMentionDropdown && mentionCandidates.length > 0 && (
                        <div className="absolute bottom-full left-0 right-0 mb-1 max-h-48 overflow-y-auto rounded-lg border border-gray-300 dark:border-dark-200 bg-white dark:bg-dark-100 shadow-lg z-50">
                          <p className="px-3 py-2 text-xs text-gray-500 dark:text-light-500 border-b border-gray-200 dark:border-dark-200">
                            Mention a channel member
                          </p>
                          {mentionCandidates.map((member, idx) => (
                            <button
                              key={member._id}
                              type="button"
                              onClick={() => insertMention(member)}
                              onMouseEnter={() => setMentionSelectedIndex(idx)}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                                idx === mentionSelectedIndex
                                  ? "bg-primary-accent/20 text-primary-accent"
                                  : "text-gray-900 dark:text-light-100 hover:bg-gray-100 dark:hover:bg-dark-400"
                              }`}
                            >
                              <Avatar
                                src={member.photo}
                                name={member.fullNameEn}
                                size="sm"
                              />
                              <div className="min-w-0">
                                <span className="font-medium truncate block">
                                  {member.fullNameEn}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-light-500 truncate block">
                                  @{member.nickname || member.fullNameEn}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => handleSendMessage()}
                      disabled={
                        (!messageInput.trim() && !attachmentFile) ||
                        isRateLimited
                      }
                      title={
                        isRateLimited
                          ? `Rate limited. You can send messages again in ${rateLimitEndTime ? Math.ceil((rateLimitEndTime - Date.now()) / 60000) : 5} minute${rateLimitEndTime ? (Math.ceil((rateLimitEndTime - Date.now()) / 60000) !== 1 ? "s" : "") : "s"}.`
                          : "Send message"
                      }
                    >
                      {isRateLimited ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-xs">
                            {rateLimitEndTime
                              ? Math.ceil(
                                  (rateLimitEndTime - Date.now()) / 60000,
                                )
                              : 5}
                            m
                          </span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>

        {/* Members Sidebar: hidden on small screens to save space */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="card hidden lg:flex flex-col h-[calc(100vh-200px)] sm:h-[calc(100vh-220px)] lg:h-[calc(100vh-240px)] min-h-[280px]"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-light-100 mb-4 flex items-center gap-2 flex-shrink-0">
            <Users className="w-5 h-5" />
            Channel Members
          </h3>
          {!selectedChannelId ? (
            <p className="text-sm text-gray-500 dark:text-light-500">
              Select a channel to see members
            </p>
          ) : loadingMembers ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-primary-accent" />
            </div>
          ) : (
            <div className="space-y-3 overflow-y-auto flex-1 pr-2 scrollbar-thin">
              {channelMembers.map((member) => (
                <div key={member._id} className="flex items-center gap-3">
                  <div className="relative">
                    <Avatar
                      src={member.photo}
                      alt={member.fullNameEn}
                      size="sm"
                      name={member.fullNameEn}
                    />
                    <span
                      className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-primary-card ${
                        isMemberOnline(member._id)
                          ? "bg-green-500"
                          : "bg-gray-400"
                      }`}
                      title={isMemberOnline(member._id) ? "Online" : "Offline"}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-light-100 truncate">
                      {member.fullNameEn}
                      {member._id === currentUserId ? " (you)" : ""}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-light-500 truncate">
                      {Array.isArray(member.teams) && member.teams.length > 0
                        ? member.teams
                            .map((t) => t.team.replace(/_/g, " "))
                            .join(" · ")
                        : member.team
                          ? member.team.replace("_", " ")
                          : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Folder Upload Confirmation Modal */}
      <BaseModal
        isOpen={!!folderConfirmation}
        onClose={clearFolderFiles}
        title="Upload Folder as ZIP"
        subtitle={`${folderConfirmation?.fileCount ?? 0} file${(folderConfirmation?.fileCount ?? 0) !== 1 ? "s" : ""} from "${folderConfirmation?.name ?? ""}"`}
        size="sm"
      >
        <div className="space-y-4">
          <div className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 dark:border-dark-200 bg-gray-50 dark:bg-dark-100 px-3 py-2 space-y-1">
            {folderConfirmation?.files.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-sm text-gray-700 dark:text-light-200"
              >
                <FileText className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                <span className="truncate">
                  {f.webkitRelativePath || f.name}
                </span>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 dark:text-light-400">
            All files will be compressed into{" "}
            <span className="font-medium text-gray-700 dark:text-light-200">
              {folderConfirmation?.name}.zip
            </span>{" "}
            and sent as a single attachment.
          </p>
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={clearFolderFiles}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-dark-200 text-gray-700 dark:text-light-200 hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmFolder}
              className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors inline-flex items-center gap-2"
            >
              <FolderOpen className="w-4 h-4" />
              Zip &amp; Send
            </button>
          </div>
        </div>
      </BaseModal>

      {/* Create Channel Modal */}
      <BaseModal
        isOpen={isCreateChannelModalOpen}
        onClose={() => {
          setIsCreateChannelModalOpen(false);
          setNewChannel({
            name: "",
            displayName: "",
            description: "",
            memberIds: [],
            teamIds: [],
          });
        }}
        title="Create New Channel"
        subtitle="Set channel name and add members or teams"
        size="md"
      >
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Channel Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={newChannel.name}
                onChange={(e) =>
                  setNewChannel((prev) => ({
                    ...prev,
                    name: e.target.value,
                    displayName: prev.displayName || e.target.value,
                  }))
                }
                placeholder="e.g., frontend-team"
                className="w-full pl-10 pr-4 py-3 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg text-gray-900 dark:text-light-100"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Display Name <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              value={newChannel.displayName}
              onChange={(e) =>
                setNewChannel((prev) => ({
                  ...prev,
                  displayName: e.target.value,
                }))
              }
              placeholder="e.g., Frontend Team"
              className="w-full px-4 py-3 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg text-gray-900 dark:text-light-100 placeholder-gray-400 dark:placeholder-light-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Description <span className="text-gray-500">(optional)</span>
            </label>
            <textarea
              value={newChannel.description}
              onChange={(e) =>
                setNewChannel((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="What is this channel about?"
              rows={2}
              className="w-full px-4 py-3 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg resize-none text-gray-900 dark:text-light-100 placeholder-gray-400 dark:placeholder-light-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Add members <span className="text-gray-500">(optional)</span>
            </label>
            <div className="relative mb-2" ref={membersDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setIsMembersDropdownOpen(!isMembersDropdownOpen);
                  setIsTeamsDropdownOpen(false);
                }}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-dark-200 bg-white dark:bg-primary-card text-left flex items-center justify-between text-gray-900 dark:text-light-100"
              >
                <span className="text-sm">
                  {(newChannel.memberIds?.length || 0) > 0
                    ? `${newChannel.memberIds.length} member(s) selected`
                    : "Select employees"}
                </span>
                <ChevronDown className="w-5 h-5 text-gray-400" />
              </button>
              {isMembersDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-primary-card border rounded-lg shadow-lg max-h-48 overflow-hidden">
                  <div className="sticky top-0 p-2 bg-white dark:bg-primary-card border-b border-gray-300 dark:border-dark-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={createChannelMemberSearch}
                        onChange={(e) =>
                          setCreateChannelMemberSearch(e.target.value)
                        }
                        placeholder="Search employees..."
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-dark-200 bg-white dark:bg-dark-100 text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {loadingEmployees ? (
                      <div className="p-3 flex justify-center">
                        <Loader2 className="w-5 h-5 animate-spin" />
                      </div>
                    ) : (
                      employees
                        .filter((emp) => emp._id !== currentUserId)
                        .filter(
                          (emp) =>
                            emp.fullNameEn
                              ?.toLowerCase()
                              .includes(
                                createChannelMemberSearch.toLowerCase(),
                              ) ||
                            (Array.isArray(emp.teams)
                              ? emp.teams.some((t) =>
                                  t.team
                                    .toLowerCase()
                                    .includes(
                                      createChannelMemberSearch.toLowerCase(),
                                    ),
                                )
                              : emp.team
                                  ?.toLowerCase()
                                  .includes(
                                    createChannelMemberSearch.toLowerCase(),
                                  )),
                        )
                        .map((emp) => {
                          const isSelected = (newChannel.memberIds || []).some(
                            (m) => m?.toString?.() === emp._id?.toString?.(),
                          );
                          return (
                            <label
                              key={emp._id}
                              className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-200 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={!!isSelected}
                                onChange={() => toggleMember(emp._id)}
                                className="w-4 h-4 text-primary-accent rounded"
                              />
                              <span className="text-sm text-gray-900 dark:text-light-100">
                                {emp.fullNameEn}{" "}
                                {Array.isArray(emp.teams) &&
                                emp.teams.length > 0
                                  ? `(${emp.teams.map((t) => t.team.replace(/_/g, " ")).join(", ")})`
                                  : emp.team
                                    ? `(${emp.team})`
                                    : ""}
                              </span>
                            </label>
                          );
                        })
                    )}
                  </div>
                </div>
              )}
            </div>
            {(newChannel.memberIds?.length || 0) > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {newChannel.memberIds.map((id) => {
                  const emp = employees.find(
                    (e) => e._id?.toString?.() === id?.toString?.(),
                  );
                  if (!emp) return null;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm"
                    >
                      {emp.fullNameEn}
                      <button type="button" onClick={() => removeMember(id)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Add teams <span className="text-gray-500">(optional)</span>
            </label>
            <div className="relative mb-2" ref={teamsDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setIsTeamsDropdownOpen(!isTeamsDropdownOpen);
                  setIsMembersDropdownOpen(false);
                }}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-dark-200 bg-white dark:bg-primary-card text-left flex items-center justify-between text-gray-900 dark:text-light-100"
              >
                <span className="text-sm">
                  {(newChannel.teamIds?.length || 0) > 0
                    ? `${newChannel.teamIds.length} team(s) selected`
                    : "Select teams"}
                </span>
                <ChevronDown className="w-5 h-5 text-gray-400" />
              </button>
              {isTeamsDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-primary-card border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {TEAMS_OPTIONS.map((team) => (
                    <label
                      key={team.value}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-200 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={(newChannel.teamIds || []).includes(
                          team.value,
                        )}
                        onChange={() => toggleTeam(team.value)}
                        className="w-4 h-4 text-primary-accent rounded"
                      />
                      <span className="text-sm text-gray-900 dark:text-light-100">
                        {team.label}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {(newChannel.teamIds?.length || 0) > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {newChannel.teamIds.map((teamValue) => {
                  const team = TEAMS_OPTIONS.find((t) => t.value === teamValue);
                  if (!team) return null;
                  return (
                    <span
                      key={teamValue}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm"
                    >
                      {team.label}
                      <button
                        type="button"
                        onClick={() => removeTeam(teamValue)}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Add at least one member or one team. Only channel members can see
              and send messages.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-100">
            <Button
              variant="bordered"
              onClick={() => setIsCreateChannelModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateChannel}
              disabled={
                creating ||
                !newChannel.name?.trim() ||
                ((newChannel.memberIds?.length || 0) === 0 &&
                  (newChannel.teamIds?.length || 0) === 0)
              }
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Create Channel"
              )}
            </Button>
          </div>
        </div>
      </BaseModal>

      {/* Delete Channel Confirmation Modal */}
      <BaseModal
        isOpen={deleteConfirmModal.open}
        onClose={() => setDeleteConfirmModal({ open: false, channelId: null })}
        title="Delete Channel"
        subtitle="This action cannot be undone"
        size="md"
        footer={
          <>
            <Button
              variant="bordered"
              onClick={() =>
                setDeleteConfirmModal({ open: false, channelId: null })
              }
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteChannel}
              disabled={deleting}
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Delete Channel"
              )}
            </Button>
          </>
        }
      >
        <p className="text-gray-700 dark:text-light-200">
          Are you sure you want to delete this channel? All messages in this
          channel will be permanently deleted.
        </p>
      </BaseModal>

      {/* Delete Message Confirmation Modal */}
      <BaseModal
        isOpen={deleteMessageModal.open}
        onClose={() => setDeleteMessageModal({ open: false, messageId: null })}
        title="Delete Message"
        subtitle="This action cannot be undone"
        size="sm"
        footer={
          <>
            <Button
              variant="bordered"
              onClick={() =>
                setDeleteMessageModal({ open: false, messageId: null })
              }
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => handleDeleteMessage(deleteMessageModal.messageId)}
            >
              Delete Message
            </Button>
          </>
        }
      >
        <p className="text-gray-700 dark:text-light-200">
          Are you sure you want to delete this message? This action cannot be
          undone.
        </p>
      </BaseModal>

      {/* Seen By Modal */}
      {seenModal.open &&
        seenModal.msg &&
        (() => {
          const m = seenModal.msg;
          const isOneOnOne = channelMembers.length === 2;
          const readByIds = (m.readBy || []).map((r) =>
            String(r.userId?._id || r.userId),
          );
          const seenByOthers = (m.readBy || []).filter(
            (r) => String(r.userId?._id || r.userId) !== String(currentUserId),
          );
          const deliveredNotSeen = channelMembers.filter(
            (mem) =>
              String(mem._id) !== String(currentUserId) &&
              !readByIds.includes(String(mem._id)),
          );
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center"
              onMouseDown={() => setSeenModal({ open: false, msg: null })}
            >
              <div
                className="bg-white dark:bg-dark-100 rounded-2xl shadow-2xl border border-gray-200 dark:border-dark-200 w-80 max-h-[70vh] flex flex-col overflow-hidden"
                onMouseDown={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-dark-300">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-light-100">
                    Message Info
                  </h3>
                  <button
                    onClick={() => setSeenModal({ open: false, msg: null })}
                    className="w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-300 hover:text-gray-600 dark:hover:text-light-200 transition-colors text-base leading-none"
                  >
                    ✕
                  </button>
                </div>

                <div className="overflow-y-auto flex-1">
                  {/* Seen By section */}
                  <div className="px-4 pt-3 pb-2">
                    <div className="flex items-center gap-1.5 mb-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-primary-accent" />
                      <span className="text-xs font-semibold text-primary-accent uppercase tracking-wide">
                        Seen by
                      </span>
                      <span className="ml-auto text-xs text-gray-400 dark:text-light-500">
                        {seenByOthers.length}
                      </span>
                    </div>
                    {seenByOthers.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-light-500 py-1">
                        No one has seen this yet
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {seenByOthers.map((r, idx) => {
                          const member = channelMembers.find(
                            (mem) =>
                              String(mem._id) ===
                              String(r.userId?._id || r.userId),
                          );
                          return (
                            <div
                              key={idx}
                              className="flex items-center gap-2.5 py-1"
                            >
                              <Avatar
                                src={member?.photo}
                                name={member?.fullNameEn || "?"}
                                size="xs"
                              />
                              <span className="text-sm text-gray-700 dark:text-light-300 truncate flex-1">
                                {member?.fullNameEn || "?"}
                              </span>
                              {r.readAt && (
                                <span className="text-[10px] text-gray-400 dark:text-light-500 shrink-0">
                                  {new Date(r.readAt).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {!isOneOnOne && (
                    <>
                      <div className="border-t border-gray-100 dark:border-dark-300 mx-4" />

                      {/* Delivered To section */}
                      <div className="px-4 pt-3 pb-4">
                        <div className="flex items-center gap-1.5 mb-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-gray-400 dark:text-light-500" />
                          <span className="text-xs font-semibold text-gray-400 dark:text-light-500 uppercase tracking-wide">
                            Delivered to
                          </span>
                          <span className="ml-auto text-xs text-gray-400 dark:text-light-500">
                            {deliveredNotSeen.length}
                          </span>
                        </div>
                        {deliveredNotSeen.length === 0 ? (
                          <p className="text-xs text-gray-400 dark:text-light-500 py-1">
                            Everyone has seen this
                          </p>
                        ) : (
                          <div className="space-y-1.5">
                            {deliveredNotSeen.map((mem, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2.5 py-1"
                              >
                                <Avatar
                                  src={mem.photo}
                                  name={mem.fullNameEn}
                                  size="xs"
                                />
                                <span className="text-sm text-gray-500 dark:text-light-400 truncate flex-1">
                                  {mem.fullNameEn}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      {/* Add Members Modal */}
      <BaseModal
        isOpen={isAddMembersModalOpen}
        onClose={() => {
          setIsAddMembersModalOpen(false);
          setSelectedMembersToAdd([]);
        }}
        title="Add Members to Channel"
        subtitle="Select employees to add to this channel"
        size="md"
        footer={
          <>
            <Button
              variant="bordered"
              onClick={() => {
                setIsAddMembersModalOpen(false);
                setSelectedMembersToAdd([]);
              }}
              disabled={addingMembers}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMembersToChannel}
              disabled={addingMembers || selectedMembersToAdd.length === 0}
            >
              {addingMembers ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                `Add ${selectedMembersToAdd.length} Member${selectedMembersToAdd.length !== 1 ? "s" : ""}`
              )}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Select employees to add
            </label>
            <div className="border border-gray-300 dark:border-dark-200 rounded-lg max-h-64 overflow-hidden">
              <div className="sticky top-0 p-2 bg-white dark:bg-primary-card border-b border-gray-300 dark:border-dark-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={addMemberSearch}
                    onChange={(e) => setAddMemberSearch(e.target.value)}
                    placeholder="Search employees..."
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-dark-200 bg-white dark:bg-dark-100 text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent"
                  />
                </div>
              </div>
              {loadingEmployees ? (
                <div className="p-4 flex justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-primary-accent" />
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-dark-200 max-h-52 overflow-y-auto">
                  {employees
                    .filter(
                      (emp) =>
                        emp._id !== currentUserId &&
                        !channelMembers.some(
                          (m) => m._id?.toString() === emp._id?.toString(),
                        ),
                    )
                    .filter(
                      (emp) =>
                        emp.fullNameEn
                          ?.toLowerCase()
                          .includes(addMemberSearch.toLowerCase()) ||
                        (Array.isArray(emp.teams)
                          ? emp.teams.some((t) =>
                              t.team
                                .toLowerCase()
                                .includes(addMemberSearch.toLowerCase()),
                            )
                          : emp.team
                              ?.toLowerCase()
                              .includes(addMemberSearch.toLowerCase())),
                    )
                    .map((emp) => {
                      const isSelected = selectedMembersToAdd.some(
                        (m) => m?.toString() === emp._id?.toString(),
                      );
                      return (
                        <label
                          key={emp._id}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-200 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleMemberToAdd(emp._id)}
                            className="w-4 h-4 text-primary-accent rounded"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-light-100 block">
                              {emp.fullNameEn}
                            </span>
                            {emp.team ||
                            (Array.isArray(emp.teams) &&
                              emp.teams.length > 0) ? (
                              <span className="text-xs text-gray-500 dark:text-light-500">
                                {Array.isArray(emp.teams) &&
                                emp.teams.length > 0
                                  ? emp.teams
                                      .map((t) => t.team.replace(/_/g, " "))
                                      .join(" · ")
                                  : emp.team.replace("_", " ")}
                              </span>
                            ) : null}
                          </div>
                        </label>
                      );
                    })}
                  {employees.filter(
                    (emp) =>
                      emp._id !== currentUserId &&
                      !channelMembers.some(
                        (m) => m._id?.toString() === emp._id?.toString(),
                      ),
                  ).length === 0 && (
                    <p className="p-4 text-sm text-gray-500 dark:text-light-500 text-center">
                      All employees are already members of this channel
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          {selectedMembersToAdd.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
                Selected ({selectedMembersToAdd.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedMembersToAdd.map((id) => {
                  const emp = employees.find(
                    (e) => e._id?.toString() === id?.toString(),
                  );
                  if (!emp) return null;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm"
                    >
                      {emp.fullNameEn}
                      <button
                        type="button"
                        onClick={() => toggleMemberToAdd(id)}
                        className="hover:text-blue-600 dark:hover:text-blue-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </BaseModal>
    </PageWrapper>
  );
};

export default GroupChat;
